import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  BarChart3,
  Calendar,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Database,
  DollarSign,
  Factory,
  Info,
  Lightbulb,
  MessageSquare,
  RefreshCw,
  Send,
  Sparkles,
  TrendingUp,
  Users,
  X,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/UI/alert-dialog';
import { FeedbackModal } from '@/financeBotV2/src/components/dashboard/FeedbackModal';
import { LineageContext, LineagePanel } from '@/financeBotV2/src/components/dashboard/LineagePanel';
import type { ChartData, KPIMetric } from '@/financeBotV2/src/types/dashboard';
import { preCannedReportsApi } from '@/services/preCannedReportsService';

export type SmartMorningControlOption = {
  label: string;
  value: string;
};

type SmartMorningLeadersDashboardProps = {
  response: Record<string, unknown>;
  periodOptions?: SmartMorningControlOption[];
  comparisonOptions?: SmartMorningControlOption[];
  onPeriodChange?: (value: string) => void;
  onComparisonChange?: (value: string) => void;
  controlsDisabled?: boolean;
  loading?: boolean;
  onForceRefresh?: () => void;
  forceRefreshDisabled?: boolean;
  initialTab?: 'finance' | 'commercial' | 'operations' | 'ar';
  onTabChange?: (tab: 'finance' | 'commercial' | 'operations' | 'ar') => void;
  activeTab?: 'finance' | 'commercial' | 'operations' | 'ar';
  onFinanceClick?: () => void;
  onCommercialClick?: () => void;
  onOperationsClick?: () => void;
  onArClick?: () => void;
  contentOverride?: ReactNode;
  arFilterControls?: ReactNode;
  
};

type Block = {
  id?: string;
  type?: string;
  heading?: string;
  data?: Record<string, any>;
};

type Metric = {
  id: string;
  name: string;
  value: number | null;
  unit?: string;
  formatted_value?: string;
  availability?: string;
  status?: string;
  source_field?: string;
  methodology?: string;
  lineage_v2?: LineageV2;
  scope_note?: string;
  comparators?: Array<{
    basis?: string;
    label?: string;
    delta_abs?: number | null;
    delta_pct?: number | null;
  }>;
  reasoning?: string;
};

type LineageV2Value = {
  slot: string;
  label: string;
  value_raw?: number | string | null;
  column: string;
  column_label: string;
  source: string;
  derivation?: {
    formula?: string;
    formula_pretty?: string;
  } | null;
  odata_ref?: string | null;
};

type LineageV2OdataRef = {
  id: string;
  url?: string;
  select?: string[];
  filter?: string;
  group_by?: string[];
  order_by?: string | null;
  top?: number | null;
  params?: Record<string, unknown>;
  model_name?: string;
  row_count?: number | null;
};

type LineageV2 = {
  what_it_is?: string;
  values?: LineageV2Value[];
  odata_refs?: LineageV2OdataRef[];
};

type RankingMode = 'top' | 'bottom';
type SmartMorningDashboardId = 'finance' | 'commercial' | 'operations' | 'ar';

const brand = '#7A003C';
const orange = '#F47B20';
const positive = '#0E8F5A';
const negative = '#C62843';
const SHOW_EXECUTIVE_INSIGHTS = false;
const SHOW_ASK_SMARTMORNING = false;

const asRecord = (value: unknown): Record<string, any> => (
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, any> : {}
);

const asBlocks = (response: Record<string, unknown>): Block[] => (
  Array.isArray(response.blocks) ? response.blocks as Block[] : []
);

const formatLastUpdated = (value: unknown): string | null => {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const blockById = (blocks: Block[], id: string): Block | undefined => (
  blocks.find((block) => block.id === id)
);

const compactCurrency = (value: number | null | undefined, suffix = '') => {
  if (value === null || value === undefined || Number.isNaN(value)) return '--';
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(value % 1_000_000 === 0 ? 0 : 1)}M`;
  if (abs >= 1_000) return `$${(value / 1_000).toFixed(value % 1_000 === 0 ? 0 : 1)}K`;
  return `$${value.toLocaleString()}${suffix}`;
};

// AR dashboard only: local-currency amounts when Region/Currency is Canada, UK or Belgium.
// Other dashboards never return these units, so they keep using compactCurrency.
const LOCAL_CURRENCY_PREFIX: Record<string, string> = { CAD: 'CA$', GBP: '£', EUR: '€' };
const isLocalCurrencyUnit = (unit?: string | null) => Boolean(unit && LOCAL_CURRENCY_PREFIX[unit]);
const compactLocalCurrency = (value: number, unit: string) => (
 compactCurrency(value).replace('$', LOCAL_CURRENCY_PREFIX[unit] || '$')
);

const compactQuantity = (value: number | null | undefined, unit = '') => {
  if (value === null || value === undefined || Number.isNaN(value)) return '--';
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  const suffix = unit ? ` ${unit}` : '';
  if (abs >= 1_000_000_000) return `${sign}${(abs / 1_000_000_000).toFixed(1)}B${suffix}`;
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(1)}M${suffix}`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(1)}K${suffix}`;
  return `${sign}${abs.toLocaleString(undefined, { maximumFractionDigits: 1 })}${suffix}`;
};

const formatMetricValue = (metric?: Metric) => {
  if (!metric || metric.value === null || metric.value === undefined || Number.isNaN(Number(metric.value))) return '--';
  const value = Number(metric.value);
  const unit = String(metric.unit_symbol || metric.unit || '').trim();
  if (metric.id === 'net_revenue') return compactCurrency(value);
  if (metric.id === 'product_contribution_pct') return `${value.toFixed(1)}%`;
  if (metric.id === 'cash_conversion_cycle_days') return `${value.toFixed(1)} Days`;
  if (metric.id === 'direct_labor_productivity') return `${value.toFixed(1)} lbs / DL Hour`;
  if (metric.id === 'mfg_cost_per_lb') return `$${value.toFixed(2)}`;
  if (metric.id === 'muv_dollars') return compactCurrency(value);
  if (metric.id === 'freight_cost_pct_sales') return `${value.toFixed(1)}%`;
  if (metric.unit === '%') return `${value.toFixed(1)}%`;
  if (metric.unit === 'USD') return compactCurrency(value);
  if (isLocalCurrencyUnit(metric.unit)) return compactLocalCurrency(value, String(metric.unit));
  if (['CS', 'CASE', 'CASES'].includes(unit.toUpperCase())) return compactQuantity(value, unit.toUpperCase());
  if (metric.unit === 'USD / CS') return `${compactCurrency(value)} / CS`;
  return metric.formatted_value || String(value);
};

const metricIsLoaded = (metric?: Metric) => (
  metric?.value !== null && metric?.value !== undefined
);

const metricIsComingSoon = (metric?: Metric) => (
  String(metric?.availability || metric?.status || '').toLowerCase() === 'coming_soon'
);

const hasMetricLineage = (metric?: Metric) => Boolean(
  metric
  && !metricIsComingSoon(metric)
  && metric.value !== null
  && metric.value !== undefined
  && (
    metric.methodology
    || metric.source_field
    || (metric.lineage_v2?.values?.length ?? 0) > 0
    || (metric.lineage_v2?.odata_refs?.length ?? 0) > 0
  ),
);

const selectedComparator = (metric: Metric | undefined, comparison: string) => {
  const basis = comparisonBasis(comparison);
  return metric?.comparators?.find((comparator) => {
    const comparatorBasis = String(comparator.basis || '').toLowerCase();
    const label = String(comparator.label || '').toLowerCase();
    if (comparatorBasis === basis) return true;
    if (basis === 'plan') return label.includes('plan');
    return label.includes('ly') || label.includes('last year');
  });
};

const formatDelta = (metric?: Metric, comparison = 'Last Year') => {
  if (!metric || metric.value === null || metric.value === undefined) return '--';
  const comparator = selectedComparator(metric, comparison);
  if (!comparator) return '--';
  const basis = comparator.label || `vs ${comparison}`;
  if (metric.id === 'net_revenue' && comparator.delta_pct !== null && comparator.delta_pct !== undefined) {
    return `${Math.abs(comparator.delta_pct).toFixed(1)}% ${basis}`;
  }
  if (metric.id === 'product_contribution_pct' && comparator.delta_abs !== null && comparator.delta_abs !== undefined) {
    return `${Math.abs(comparator.delta_abs).toFixed(1)}pp ${basis}`;
  }
  if (metric.id === 'cash_conversion_cycle_days' && comparator.delta_abs !== null && comparator.delta_abs !== undefined) {
    return `${Math.abs(comparator.delta_abs).toFixed(1)} Days ${basis}`;
  }
  if (metric.id === 'direct_labor_productivity' && comparator.delta_pct !== null && comparator.delta_pct !== undefined) {
    return `${Math.abs(comparator.delta_pct).toFixed(1)}% ${basis}`;
  }
  if (metric.id === 'mfg_cost_per_lb' && comparator.delta_abs !== null && comparator.delta_abs !== undefined) {
    return `$${Math.abs(comparator.delta_abs).toFixed(2)} ${basis}`;
  }
  if (metric.unit === '%' && comparator.delta_abs !== null && comparator.delta_abs !== undefined) {
    return `${Math.abs(comparator.delta_abs).toFixed(1)}pp ${basis}`;
  }
  if (metric.unit === 'USD' && comparator.delta_abs !== null && comparator.delta_abs !== undefined) {
    return `${compactCurrency(Math.abs(comparator.delta_abs))} ${basis}`;
  }
  if (isLocalCurrencyUnit(metric.unit) && comparator.delta_abs !== null && comparator.delta_abs !== undefined) {
   return `${compactLocalCurrency(Math.abs(comparator.delta_abs), String(metric.unit))} ${basis}`;
  }
  if (metric.unit === 'CS' && comparator.delta_abs !== null && comparator.delta_abs !== undefined) {
    return `${compactQuantity(Math.abs(comparator.delta_abs), 'CS')} ${basis}`;
  }
  return basis;
};

const isPositiveDelta = (metric?: Metric, comparison = 'Last Year') => {
  if (!metric || metric.value === null || metric.value === undefined) return true;
  const comparator = selectedComparator(metric, comparison);
  const delta = comparator?.delta_abs ?? comparator?.delta_pct ?? 0;
  if (['cash_conversion_cycle_days', 'mfg_cost_per_lb', 'muv_dollars', 'freight_cost_pct_sales', 'ponc_dollars', 'raw_packaging_inventory',
  'raw_packaging_inventory_usd'].includes(metric.id)) {
    return delta <= 0;
  }
  return delta >= 0;
};

const UnavailableTooltip = () => (
  <span className="pointer-events-none absolute left-1/2 top-2 z-20 -translate-x-1/2 whitespace-nowrap rounded-md bg-[#2B2027] px-2 py-1 text-xs font-semibold text-white opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
    Unavailable
  </span>
);

const InfoButton = ({ label, onClick }: { label: string; onClick?: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    className={`inline-flex h-4 w-4 shrink-0 items-center justify-center text-[#7B6B72] transition-colors ${onClick ? 'hover:text-[#7A003C]' : 'cursor-default opacity-40'}`}
    aria-label={`View information for ${label}`}
    title="View definition, calculation, and source"
  >
    <Info className="h-3 w-3" />
  </button>
);

const KpiLabel = ({ label, showInfo = false, onShowInfo, className = '' }: {
  label: string;
  showInfo?: boolean;
  onShowInfo?: () => void;
  className?: string;
}) => (
  <div className={`inline-flex items-center gap-1 ${className}`}>
    <span>{label}</span>
    {showInfo && <InfoButton label={label} onClick={onShowInfo} />}
  </div>
);

const MetricInfoButton = ({ metric, onClick }: { metric?: Metric; onClick?: (metric: Metric) => void }) => {
  if (!metric || !onClick || !hasMetricLineage(metric)) return null;
  return (
    <span className="group/info relative inline-flex">
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onClick(metric);
        }}
        aria-label={`View definition, calculation, and source for ${metric.name}`}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-[#B9A5AF] bg-[#FAF7F1] text-[#7B6B72] transition-colors hover:border-[#7A003C] hover:text-[#7A003C]"
      >
        <Info className="h-3 w-3" />
      </button>
      <span className="pointer-events-none absolute left-1/2 top-full z-40 mt-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-[#2B2027] px-2 py-1 text-xs font-semibold normal-case tracking-normal text-white opacity-0 shadow-md transition-opacity group-hover/info:opacity-100">
        View definition, calculation, and source
      </span>
    </span>
  );
};

const MetricCard = ({
 metric,
 icon: Icon,
 comparison,
 onShowInfo,
 comingSoon = false,
 loading = false,
 caption,
}: {
 metric?: Metric;
 icon: any;
 comparison: string;
 onShowInfo?: (metric: Metric) => void;
 comingSoon?: boolean;
 loading?: boolean;
 caption?: string;
}) => {
  const good = isPositiveDelta(metric, comparison);
  const name = metric?.id === 'net_revenue' ? 'Net Sales (USD)' : metric?.name || 'Metric';
  const hasValue = metric?.value !== null && metric?.value !== undefined;
  const unavailable = !loading && !hasValue;
  const muted = unavailable;
  const delta = formatDelta(metric, comparison);
  const showDelta = hasValue && delta !== '--' && !delta.toLowerCase().includes('unavailable');
  return (
    <div
      className={`group relative flex min-h-[112px] items-center gap-4 rounded-md border p-4 ${
        muted ? 'cursor-not-allowed border-[#E5E7EB] bg-[#F5F5F5]' : 'border-[#EDE6E1] bg-white'
      }`}
      aria-label={`${name}: ${unavailable ? 'Unavailable' : ''}`}
    >
      {unavailable && <UnavailableTooltip />}
      <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-2 ${
        muted ? 'border-[#B8B1B5] text-[#9B9498]' : 'border-[#7A003C] text-[#7A003C]'
      }`}
      >
        <Icon className="h-7 w-7" />
      </div>
      <div className="relative min-w-0 flex-1">
        <KpiLabel
          label={name}
          showInfo={!muted && Boolean(metric)}
          onShowInfo={metric ? () => onShowInfo?.(metric) : undefined}
          className={`text-[11px] font-bold uppercase tracking-wide ${muted ? 'text-[#8E858A]' : 'text-[#2B2027]'}`}
        />
        <div className={`mt-1 text-2xl font-bold ${muted ? 'text-[#8E858A]' : 'text-[#1F1A20]'}`}>{loading ? 'Loading…' : formatMetricValue(metric)}</div>
        <div className={`mt-1 min-h-[16px] text-xs font-bold ${muted ? 'text-[#9B8B92]' : showDelta ? (good ? 'text-[#0E8F5A]' : 'text-[#C62843]') : 'text-[#9B8B92]'}`}>
          {loading ? 'Fetching data' : !hasValue ? 'Unavailable' : showDelta ? `${good ? '▲' : '▼'} ${delta}` : (caption || '')}
        </div>
      </div>
    </div>
  );
};

const SectionHeader = ({ number, title, subtitle, icon: Icon }: { number: string; title: string; subtitle: string; icon: any }) => (
  <div className="flex items-center gap-4 rounded-t-md bg-[#7A003C] px-4 py-3 text-white">
    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-[#7A003C]">
      <Icon className="h-7 w-7" />
    </div>
    <div className="text-4xl font-bold leading-none">{number}</div>
    <div>
      <div className="text-lg font-bold uppercase tracking-wide">{title}</div>
      <div className="text-xs font-medium text-white/85">{subtitle}</div>
    </div>
  </div>
);

const MiniMetric = ({
  label,
  note,
  value,
  delta,
  good,
  infoMetric,
  comingSoon = false,
  loading = false,
  metric,
  onShowInfo,
}: {
  label: string;
  note?: string;
  value: string;
  delta: string;
  good: boolean;
  infoMetric?: Metric;
  comingSoon?: boolean;
  loading?: boolean;
  metric?: Metric;
  onShowInfo?: (metric: Metric) => void;
}) => {
  const showDelta = value !== '--' && delta !== '--' && !delta.toLowerCase().includes('unavailable');
  const unavailable = !loading && value === '--';
  const muted = unavailable;
  const metricId = (metric ?? infoMetric)?.id || '';

  const useLowerIsBetterArrow = [
    'mfg_cost_per_lb',
    'raw_packaging_inventory',
    'raw_packaging_inventory_usd',
  ].includes(metricId);

  const trendArrow = useLowerIsBetterArrow
    ? (good ? '▼' : '▲')
    : (good ? '▲' : '▼');
  return (
    <div
      className={`group relative rounded-md border p-4 text-center ${
        muted ? 'cursor-not-allowed border-[#E5E7EB] bg-[#F5F5F5]' : 'border-[#EDE6E1] bg-white'
      }`}
      aria-label={`${label}: ${unavailable ? 'Unavailable' : ''}`}
    >
      {unavailable && <UnavailableTooltip />}
      <div>

        <KpiLabel
          label={label}
          showInfo={!muted && metricIsLoaded(metric ?? infoMetric)}
          onShowInfo={() => onShowInfo?.((metric ?? infoMetric) as Metric)}
          className={`text-[11px] font-bold uppercase ${muted ? 'text-[#8E858A]' : 'text-[#7B6B72]'}`}
        />
        {note && <div className="mt-0.5 text-[9px] font-semibold text-[#8E858A]">{note}</div>}
      </div>
      <div className={`mt-2 text-2xl font-bold ${muted ? 'text-[#8E858A]' : 'text-[#1F1A20]'}`}>{loading ? 'Loading…' : value}</div>
      <div className={`mt-2 min-h-[16px] text-xs font-bold ${muted ? 'text-[#9B8B92]' : showDelta ? (good ? 'text-[#0E8F5A]' : 'text-[#C62843]') : 'text-[#9B8B92]'}`}>
          {loading
    ? 'Fetching data'
    : value === '--'
      ? 'Unavailable'
      : showDelta
        ? `${trendArrow} ${delta}`
        : ''}
      </div>
    </div>
  );
};

const SimpleWaterfall = ({ rows, loading = false, chart, onShowInfo }: { rows: any[]; loading?: boolean; chart?: ChartData; onShowInfo?: (chart: ChartData) => void }) => {
  const validRows = normalizeBridgeRows(rows);
  if (!validRows.length) {
    return <div className="mt-4 rounded-md border border-dashed border-[#E6DCD6] p-6 text-sm font-semibold text-[#7B6B72]">{loading ? 'Loading bridge data…' : 'Bridge data unavailable until the configured tool returns mapped driver rows.'}</div>;
  }
  const bars = validRows.map((row) => ({
    ...row,
    value: Number(row.value || 0),
    total: row.kind === 'total',
  }));
  const values = bars.map((bar) => bar.value);
  const rawMin = Math.min(0, ...values);
  const rawMax = Math.max(0, ...values);
  const padding = Math.max((rawMax - rawMin) * 0.08, 1);
  const min = rawMin < 0 ? rawMin - padding : 0;
  const max = rawMax > 0 ? rawMax + padding : 0;
  const range = Math.max(max - min, 1);
  const width = Math.max(520, bars.length * 112);
  const height = 210;
  const top = 24;
  const bottom = 44;
  const chartHeight = height - top - bottom;
  const barWidth = 42;
  const xFor = (index: number) => 38 + index * ((width - 76) / Math.max(bars.length - 1, 1));
  const yFor = (value: number) => top + ((max - value) / range) * chartHeight;
  const zeroY = yFor(0);

  return (
    <div className="relative mt-4 overflow-x-auto">
      <div className="absolute right-2 top-1 z-10 rounded-md bg-white/90">
        <InfoButton label={chart?.title || 'Revenue / Contribution Bridge'} onClick={chart ? () => onShowInfo?.(chart) : undefined} />
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="h-[210px] min-w-full" role="img" aria-label="Net sales bridge waterfall chart">
        <line x1="16" x2={width - 16} y1={zeroY} y2={zeroY} stroke="#CFC4BE" strokeWidth="1.5" />
        <text x="18" y={zeroY - 5} className="fill-[#7B6B72] text-[10px] font-semibold">0</text>
        {bars.map((bar, index) => {
          const x = xFor(index);
          const valueY = yFor(bar.value);
          const y = bar.value >= 0 ? valueY : zeroY;
          const h = Math.max(4, Math.abs(zeroY - valueY));
          const color = bar.value >= 0 ? positive : negative;
          const formattedValue = formatBridgeValue(bar.value);
          const label = !bar.total && bar.value > 0 && !String(formattedValue).startsWith('+') ? `+${formattedValue}` : formattedValue;
          const labelY = bar.value >= 0 ? y - 8 : y + h + 16;
          const labelLines = String(bar.driver).split(/\s+/);
          const displayLines = labelLines.length > 2 ? [labelLines.slice(0, -1).join(' '), labelLines[labelLines.length - 1]] : labelLines;
          return (
            <g key={`${bar.driver}-${index}`}>
              <text x={x} y={labelY} textAnchor="middle" className={`fill-[#2B2027] text-[12px] font-bold ${!bar.total && bar.value < 0 ? 'fill-[#C62843]' : ''}`}>
                {label}
              </text>
              <rect x={x - barWidth / 2} y={y} width={barWidth} height={h} rx="3" fill={color} />
              {displayLines.map((line, lineIndex) => (
                <text key={lineIndex} x={x} y={height - 20 + lineIndex * 12} textAnchor="middle" className="fill-[#4B3C44] text-[10px] font-semibold">
                  {line}
                </text>
              ))}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

const normalizeBridgeRows = (rows: any[]) => {
  const normalized = rows
    .filter((row) => row?.driver && Number.isFinite(Number(row?.value)))
    .map((row, index) => {
      const rawDriver = String(row.driver);
      const key = rawDriver.toLowerCase().replace(/[^a-z]+/g, ' ').trim();
      const isBase = ['base', 'ly', 'last year', 'plan', 'plan ytd'].includes(key);
      const isActual = ['actual', 'actual ytd', 'current year', 'current'].includes(key);
      const driver = key === 'price mix' ? 'Price / Mix' : rawDriver;
      return {
        ...row,
        driver,
        value: Number(row.value),
        kind: isBase || isActual || row.kind === 'total' ? 'total' : 'driver',
        bridgeOrder: bridgeOrder(key, index),
      };
    });
  return normalized.sort((a, b) => a.bridgeOrder - b.bridgeOrder);
};

const bridgeOrder = (key: string, index: number) => {
  if (['base', 'ly', 'last year', 'plan', 'plan ytd'].includes(key)) return 0;
  if (key === 'price' || key === 'price mix') return 1;
  if (key === 'mix') return 2;
  if (key === 'volume') return 3;
  if (['actual', 'actual ytd', 'current year', 'current'].includes(key)) return 99;
  return 10 + index;
};

const formatBridgeValue = (value: number) => {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000_000) return `${sign}$${(abs / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(1)}`;
};

const firstValue = (row: Record<string, any>, keys: string[]) => {
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null && row[key] !== '') return row[key];
  }
  return undefined;
};

const rankingLabelGroups = [
  ['Ranking_Item'],
  ['End_Customer_End_Customer', 'Customer', 'customer', 'customer_name', 'Sold_To_Party_T', 'Sold_To_Party', 'End_Customer'],
  ['ZMATERIAL_T', 'ZMATERIAL', 'Material_Material_T', 'Material_Material', 'Material', 'material', 'material_name'],
  ['Prod_Class_Mat_Sales_Comp_T', 'Product_Class', 'product_class', 'Category'],
  ['dimension_label', 'name', 'label'],
];

const productLabelGroups = [
  ['Product'],
  ['ZMATERIAL_T', 'Material_Material_T', 'material_name'],
  ['ZMATERIAL', 'Material_Material', 'Material', 'material'],
];

const labelIsMissing = (value: unknown) => {
  const normalized = normalizeLabel(value).toLowerCase();
  return !normalized || normalized === '--' || normalized === 'unassigned' || normalized === 'null' || normalized === 'undefined';
};

const normalizeLabel = (value: unknown) => (
  String(value ?? '')
    .replace(/\u00a0/g, ' ')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
);

const canonicalLabelKey = (value: unknown) => {
  const normalized = normalizeLabel(value).toLowerCase();
  return labelIsMissing(normalized) ? 'unassigned' : normalized;
};

const operationDisplayLabel = (row: Record<string, any>, labelGroups = rankingLabelGroups) => {
  for (const keys of labelGroups) {
    const value = firstValue(row, keys);
    if (!labelIsMissing(value)) return normalizeLabel(value);
  }
  for (const keys of labelGroups) {
    const value = firstValue(row, keys);
    const normalized = normalizeLabel(value);
    if (value !== undefined && value !== null && normalized) return labelIsMissing(normalized) ? 'Unassigned' : normalized;
  }
  return 'Unassigned';
};

const numericValue = (row: Record<string, any>, keys: string[]) => {
  const value = Number(firstValue(row, keys));
  return Number.isFinite(value) ? value : 0;
};

const contributionValue = (row: Record<string, any>) => numericValue(row, ['Prod_Contri_USD', 'Product_Contribution_USD', 'pc_dollars']);

const rankedContributionRows = (rows: any[], mode: RankingMode, maxRows = 5) => (
  [...rows]
    .filter((row) => Number.isFinite(contributionValue(row)))
    .sort((a, b) => (
      mode === 'top'
        ? contributionValue(b) - contributionValue(a)
        : contributionValue(a) - contributionValue(b)
    ))
    .slice(0, maxRows)
);

const aggregateRankingRows = (rows: any[], direction: 'growth' | 'decline') => {
  const grouped = new Map<string, Record<string, any>>();
  for (const row of rows) {
    const label = operationDisplayLabel(row);
    const key = canonicalLabelKey(label);
    const current = numericValue(row, ['Net_Sales_USD']);
    const lastYear = numericValue(row, ['Net_Sales_USD_LY', 'Net_Sales_USD__ly_shifted']);
    const delta = numericValue(row, ['Net_Sales_USD_vs_LY_USD']);
    const existing = grouped.get(key) || {
      Ranking_Item: label,
      Net_Sales_USD: 0,
      Net_Sales_USD_LY: 0,
      Net_Sales_USD_vs_LY_USD: 0,
    };
    if (existing.Ranking_Item === 'Unassigned' && label !== 'Unassigned') {
      existing.Ranking_Item = label;
    }
    existing.Net_Sales_USD += current;
    existing.Net_Sales_USD_LY += lastYear;
    existing.Net_Sales_USD_vs_LY_USD += delta;
    grouped.set(key, existing);
  }
  return Array.from(grouped.values())
    .map((row) => ({
      ...row,
      Net_Sales_USD_vs_LY_Pct: row.Net_Sales_USD_LY
        ? 100 * row.Net_Sales_USD_vs_LY_USD / Math.abs(row.Net_Sales_USD_LY)
        : null,
    }))
    .sort((a, b) => (
      direction === 'growth'
        ? b.Net_Sales_USD_vs_LY_USD - a.Net_Sales_USD_vs_LY_USD
        : a.Net_Sales_USD_vs_LY_USD - b.Net_Sales_USD_vs_LY_USD
    ))
    .slice(0, 5);
};

const formatPct = (value: unknown) => {
  const num = Number(value);
  return Number.isFinite(num) ? `${num.toFixed(1)}%` : '--';
};

const formatVolume = (rows: any[]) => {
  const row = rows[0] || {};
  const value = Number(firstValue(row, ['Actual_Units_CS', 'Volume_CS', 'Quantity_CS', 'Sales_QtyDeliveredCS', 'Delivered_Qty_CS', 'Sales_QtyDelivered']));
  if (!Number.isFinite(value)) return '-- CS';
  return compactQuantity(value, 'CS');
};

const formatVolumeComparison = (rows: any[], comparison: string) => {
  const row = rows[0] || {};
  const basis = comparisonBasis(comparison);
  const variance = Number(firstValue(row, basis === 'plan'
    ? ['Volume_vs_Plan_Units']
    : ['Volume_vs_LY_Units']));
  if (!Number.isFinite(variance)) return '--';
  const direction = variance >= 0 ? '▲' : '▼';
  return `${direction} ${formatVolume([{ Actual_Units_CS: Math.abs(variance) }])} ${comparisonLabel(comparison)}`;
};

const comparisonBasis = (comparison: string) => {
  const value = comparison.toLowerCase();
  if (value.includes('plan')) return 'plan';
  return 'ly';
};

const comparisonLabel = (comparison: string) => {
  const basis = comparisonBasis(comparison);
  if (basis === 'plan') return 'vs Plan';
  return 'vs LY';
};

const rowComparatorValue = (row: Record<string, any>, comparison: string, measure: 'units' | 'contribution') => {
  const basis = comparisonBasis(comparison);
  if (basis === 'plan') {
    return Number(firstValue(row, measure === 'units'
      ? ['Plan_Units_CS', 'Actual_Units_CS_PLAN']
      : ['Plan_Prod_Contribution_USD', 'Prod_Contri_USD_PLAN', 'Plan_Prod_Contri_USD', 'Plan_Product_Contribution_USD']));
  }
  return Number(firstValue(row, measure === 'units'
    ? ['Actual_Units_CS_LY']
    : ['Prod_Contri_USD_LY', 'Product_Contribution_USD_LY']));
};

const NumberTable = ({ rows, kind, maxRows, comparison, loading = false, info, onShowInfo }: { rows: any[]; kind: 'customers' | 'channels'; maxRows: number; comparison: string; loading?: boolean; info?: ChartData; onShowInfo?: (chart: ChartData) => void }) => (
  <div className="relative">
    {info && <div className="absolute right-0 top-[-31px] z-10 rounded-md bg-white/90"><InfoButton label={info.title} onClick={() => onShowInfo?.(info)} /></div>}
    <table className="mt-3 w-full text-left text-xs">
    <thead className="text-[10px] uppercase text-[#7B6B72]">
      <tr>
        <th className="py-1">{kind === 'customers' ? 'Customer' : 'Channel'}</th>
        <th className="py-1 text-right">PC $</th>
        <th className="py-1 text-right">PC %</th>
        <th className="py-1 text-right">Compare</th>
      </tr>
    </thead>
    <tbody className="font-semibold text-[#2B2027]">
      {rows.length === 0 ? (
        <tr className="border-t border-[#F1ECE8]">
          <td className="py-3 text-[#7B6B72]" colSpan={4}>{loading ? 'Loading data…' : 'Unavailable until the configured tool returns rows for this grain.'}</td>
        </tr>
      ) : rows.slice(0, maxRows).map((row) => {
        const label = firstValue(row, kind === 'customers'
          ? ['End_Customer_End_Customer', 'customer', 'Customer', 'customer_name', 'Sold_To_Party_T', 'Sold_To_Party']
          : ['Profit_Center_Cur_T', 'Profit_Center_Cur', 'channel', 'Channel']);
        const pcDollars = Number(firstValue(row, ['pc_dollars', 'Prod_Contri_USD', 'Product_Contribution_USD']));
        const pcPct = firstValue(row, ['pc_pct', 'Product_Contribution_Pct', 'Product_Contribution_Pct_LY']);
        const comparatorValue = rowComparatorValue(row, comparison);
        const compare = Number.isFinite(pcDollars) && Number.isFinite(comparatorValue) ? pcDollars - comparatorValue : Number.NaN;
        const hasCompare = Number.isFinite(compare) && comparatorValue !== 0;
        return (
          <tr key={String(label || JSON.stringify(row))} className="border-t border-[#F1ECE8]">
            <td className="py-1.5">{String(label || 'Unassigned')}</td>
            <td className="py-1.5 text-right">{Number.isFinite(pcDollars) ? compactCurrency(pcDollars) : '--'}</td>
            <td className="py-1.5 text-right">{formatPct(pcPct)}</td>
            <td className={`py-1.5 text-right ${!hasCompare ? 'text-[#9B8B92]' : compare >= 0 ? 'text-[#0E8F5A]' : 'text-[#C62843]'}`}>
              {!hasCompare ? '--' : `${compare >= 0 ? '▲' : '▼'} ${compactCurrency(Math.abs(compare))} ${comparisonLabel(comparison)}`}
            </td>
          </tr>
        );
      })}
    </tbody>
    </table>
  </div>
);

const CommercialMetricCard = ({ label, metric, icon: Icon, comparison, loading = false, onShowInfo }: { label: string; metric?: Metric; icon: any; comparison: string; loading?: boolean; onShowInfo?: (metric: Metric) => void }) => {
  const hasValue = metricIsLoaded(metric);
  const delta = formatDelta(metric, comparison);
  const good = isPositiveDelta(metric, comparison);
  const showDelta = hasValue && delta !== '--' && !delta.toLowerCase().includes('unavailable');
  return (
    <div className={`min-h-[112px] rounded-md border p-4 ${hasValue ? 'border-[#EDE6E1] bg-white' : 'border-[#E5E7EB] bg-[#F5F5F5]'}`}>
      <div className="flex items-center gap-3">
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 ${hasValue ? 'border-[#7A003C] text-[#7A003C]' : 'border-[#B8B1B5] text-[#9B9498]'}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="relative min-w-0">
          <KpiLabel
            label={label}
            showInfo={hasValue}
            onShowInfo={() => onShowInfo?.(metric as Metric)}
            className={`text-[11px] font-bold uppercase tracking-wide ${hasValue ? 'text-[#2B2027]' : 'text-[#8E858A]'}`}
          />
          <div className={`mt-1 text-2xl font-bold ${hasValue ? 'text-[#1F1A20]' : 'text-[#8E858A]'}`}>
            {loading ? 'Loading…' : hasValue ? formatMetricValue(metric) : '--'}
          </div>
          <div className={`mt-1 text-xs font-bold ${hasValue && showDelta ? (good ? 'text-[#0E8F5A]' : 'text-[#C62843]') : hasValue ? 'text-[#0E8F5A]' : 'text-[#9B8B92]'}`}>
            {loading ? 'Fetching data' : !hasValue ? 'Unavailable' : showDelta ? `${good ? '▲' : '▼'} ${delta}` : ''}
          </div>
        </div>
      </div>
    </div>
  );
};

const CommercialTable = ({ title, rows = [], unavailable = false }: { title: string; rows?: any[]; unavailable?: boolean }) => (
  <div className="rounded-md border border-[#EDE6E1] bg-white p-4">
    <div className="text-sm font-bold uppercase text-[#7A003C]">{title}</div>
    {unavailable || rows.length === 0 ? (
      <div className="mt-3 rounded-md border border-dashed border-[#E6DCD6] p-4 text-sm font-semibold text-[#8E858A]">Coming soon</div>
    ) : (
      <div className="mt-3 text-xs font-semibold text-[#2B2027]">{rows.length} rows available</div>
    )}
  </div>
);

const CommercialRankTable = ({ title, rows = [], dimension, measure, rank, comparison, loading = false, allRows = false, info, onShowInfo }: { title: string; rows?: any[]; dimension: 'customer' | 'product' | 'category'; measure: 'units' | 'contribution'; rank: 'top' | 'bottom'; comparison: string; loading?: boolean; allRows?: boolean; info?: ChartData; onShowInfo?: (chart: ChartData) => void }) => {
  const [sortDirection, setSortDirection] = useState<'desc' | 'asc'>('desc');
  const labelKeys = dimension === 'customer'
    ? ['End_Customer_End_Customer', 'customer', 'Customer', 'customer_name', 'Sold_To_Party_T', 'Sold_To_Party']
    : dimension === 'product'
      ? ['Material_T', 'material_label', 'Material', 'ZMATERIAL_T', 'ZMATERIAL']
      : ['Prod_Class_Mat_Sales_Comp_T', 'product_class', 'Product_Class_T', 'Product_Class', 'ProductClass', 'Prod_Class_Mat_Sales_Comp'];
  const valueKey = measure === 'units' ? 'Actual_Units_CS' : 'Prod_Contri_USD';
  const rowValue = (row: any, key: string) => {
    if (measure === 'units') return row?.[key];
    return firstValue(row, key === valueKey
      ? [key, 'Product_Contribution_USD', 'pc_dollars']
      : [key, 'Product_Contribution_USD_LY', 'pc_dollars_ly']);
  };
  const varianceFor = (row: any) => Number(rowValue(row, valueKey)) - rowComparatorValue(row, comparison, measure);
  const ranked = rows
    .filter((row) => {
      const value = Number(rowValue(row, valueKey));
      const comparator = rowComparatorValue(row, comparison, measure);
      return Number.isFinite(value) && Number.isFinite(comparator);
    })
    .sort((a, b) => (sortDirection === 'desc' ? 1 : -1) * (varianceFor(b) - varianceFor(a)));
  const selected = allRows
    ? ranked
    : rank === 'top'
      ? ranked.slice(0, 5)
      : ranked.slice(-5).sort((a, b) => varianceFor(a) - varianceFor(b));
  return (
    <div className="rounded-md border border-[#EDE6E1] bg-white p-4">
      <div className="flex items-center justify-between gap-2">
        <KpiLabel label={title} showInfo={Boolean(info)} onShowInfo={info ? () => onShowInfo?.(info) : undefined} className="text-sm font-bold uppercase text-[#7A003C]" />
        {allRows && <select value={sortDirection} onChange={(event) => setSortDirection(event.target.value as 'desc' | 'asc')} className="rounded border border-[#EDE6E1] bg-white px-2 py-1 text-xs font-semibold text-[#7B6B72]" aria-label={`Sort ${title}`}><option value="desc">High to low</option><option value="asc">Low to high</option></select>}
      </div>
      <div className={allRows ? 'mt-3 max-h-[170px] overflow-y-auto' : 'mt-3'}>
      <table className="w-full text-left text-xs">
        <thead className="text-[10px] uppercase text-[#7B6B72]"><tr>
          <th className="py-1">{dimension === 'customer' ? 'Customer' : dimension === 'product' ? 'Product' : 'Product Class'}</th>
          <th className="py-1 text-right">{measure === 'units' ? 'Units' : 'PC $'}</th>
          {measure === 'contribution' && <th className="py-1 text-right">PC %</th>}
          <th className="py-1 text-right">Variance {comparisonLabel(comparison)}</th>
        </tr></thead>
        <tbody className="font-semibold text-[#2B2027]">
          {selected.length === 0 ? <tr className="border-t border-[#F1ECE8]"><td className="py-3 text-[#7B6B72]" colSpan={measure === 'contribution' ? 4 : 3}>{loading ? 'Loading data…' : 'Unavailable until the configured tool returns rows for this grain.'}</td></tr> : selected.map((row, index) => {
            const label = firstValue(row, labelKeys);
            const value = Number(rowValue(row, valueKey));
            const comparator = rowComparatorValue(row, comparison, measure);
            const delta = Number.isFinite(comparator) ? value - comparator : Number.NaN;
            return <tr key={`${String(label)}-${index}`} className="border-t border-[#F1ECE8]">
              <td className="py-1.5">{String(label || 'Unassigned')}</td>
              <td className="py-1.5 text-right">{measure === 'units' ? formatVolume([{ Actual_Units_CS: value }]) : compactCurrency(value)}</td>
              {measure === 'contribution' && <td className="py-1.5 text-right">{formatPct(firstValue(row, ['Product_Contribution_Pct', 'Product_Contribution_Pct_LY']))}</td>}
              <td className={`py-1.5 text-right ${Number.isFinite(delta) ? delta >= 0 ? 'text-[#0E8F5A]' : 'text-[#C62843]' : 'text-[#9B8B92]'}`}>
                {Number.isFinite(delta) ? `${delta >= 0 ? '▲' : '▼'} ${measure === 'units' ? formatVolume([{ Actual_Units_CS: Math.abs(delta) }]) : compactCurrency(Math.abs(delta))} ${comparisonLabel(comparison)}` : '--'}
              </td>
            </tr>;
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
};

const CommercialGrowthTable = ({ rows = [], loading = false, info, onShowInfo }: { rows?: any[]; loading?: boolean; info?: ChartData; onShowInfo?: (chart: ChartData) => void }) => {
  const [sortDirection, setSortDirection] = useState<'desc' | 'asc'>('desc');
  const ranked = rows
    .map((row) => ({
      ...row,
      pcValue: Number(firstValue(row, ['Prod_Contri_USD', 'Product_Contribution_USD', 'pc_dollars'])),
      variance: Number(firstValue(row, ['Prod_Contri_USD_vs_LY_USD', 'Product_Contribution_USD_vs_LY_USD', 'variance_usd'])),
    }))
    .filter((row) => Number.isFinite(row.variance));
  const sorted = [...ranked].sort((a, b) => (sortDirection === 'desc' ? 1 : -1) * (b.variance - a.variance));
  return (
    <div className="rounded-md border border-[#EDE6E1] bg-white p-4">
      <div className="flex items-center justify-between gap-2">
        <KpiLabel label="Ranking by Channel (LY Variance)" showInfo={Boolean(info)} onShowInfo={info ? () => onShowInfo?.(info) : undefined} className="text-sm font-bold uppercase text-[#7A003C]" />
        <select value={sortDirection} onChange={(event) => setSortDirection(event.target.value as 'desc' | 'asc')} className="rounded border border-[#EDE6E1] bg-white px-2 py-1 text-xs font-semibold text-[#7B6B72]" aria-label="Sort channel rankings">
          <option value="desc">High to low</option>
          <option value="asc">Low to high</option>
        </select>
      </div>
      <div className="mt-3 max-h-[170px] overflow-y-auto">
        <table className="w-full text-left text-xs"><thead className="text-[10px] uppercase text-[#7B6B72]"><tr><th className="py-1">Channel</th><th className="py-1 text-right">PC $</th><th className="py-1 text-right">PC LY Variance</th></tr></thead><tbody className="font-semibold text-[#2B2027]">
          {sorted.length === 0 ? <tr className="border-t border-[#F1ECE8]"><td className="py-3 text-[#7B6B72]" colSpan={3}>{loading ? 'Loading data…' : 'Coming soon'}</td></tr> : sorted.map((row, index) => <tr key={`${String(firstValue(row, ['Profit_Center_Cur_T', 'Profit_Center_Cur', 'channel', 'Channel']))}-${index}`} className="border-t border-[#F1ECE8]"><td className="py-1.5">{String(firstValue(row, ['Profit_Center_Cur_T', 'Profit_Center_Cur', 'channel', 'Channel']) || 'Unassigned')}</td><td className="py-1.5 text-right">{Number.isFinite(row.pcValue) ? compactCurrency(row.pcValue) : '--'}</td><td className={`py-1.5 text-right ${row.variance >= 0 ? 'text-[#0E8F5A]' : 'text-[#C62843]'}`}>{row.variance >= 0 ? '▲' : '▼'} {compactCurrency(Math.abs(row.variance))} vs LY</td></tr>)}
        </tbody></table>
      </div>
    </div>
  );
};

const CommercialContent = ({ topKpis, supply, bridgeRows, bridgeChart, growthRows, customerRows, customerUnitRows, productRows, productUnitRows, categoryRows, categoryUnitRows, growthInfo, customerInfo, customerUnitInfo, productInfo, productUnitInfo, categoryInfo, categoryUnitInfo, comparison, loading, onShowInfo, onShowChartInfo }: { topKpis: Metric[]; supply: Metric[]; bridgeRows: any[]; bridgeChart?: ChartData; growthRows: any[]; customerRows: any[]; customerUnitRows: any[]; productRows: any[]; productUnitRows: any[]; categoryRows: any[]; categoryUnitRows: any[]; growthInfo?: ChartData; customerInfo?: ChartData; customerUnitInfo?: ChartData; productInfo?: ChartData; productUnitInfo?: ChartData; categoryInfo?: ChartData; categoryUnitInfo?: ChartData; comparison: string; loading: boolean; onShowInfo: (metric: Metric) => void; onShowChartInfo: (chart: ChartData) => void }) => {
  const netSales = topKpis.find((metric) => metric.id === 'net_revenue');
  const grossSales = topKpis.find((metric) => metric.id === 'gross_revenue');
  const ponc = topKpis.find((metric) => metric.id === 'ponc_dollars')
    || supply.find((metric) => metric.id === 'ponc_dollars' || metric.name?.toLowerCase() === 'ponc');
  const productContributionDollars = topKpis.find((metric) => metric.id === 'product_contribution_usd')
    || supply.find((metric) => metric.id === 'product_contribution_usd');
  const productContributionPct = topKpis.find((metric) => metric.id === 'product_contribution_pct');
  const tradeSpend = topKpis.find((metric) => metric.id === 'trade_spend_usd');
  const freightRevenue = topKpis.find((metric) => metric.id === 'freight_revenue_usd');
  const freightMargin = topKpis.find((metric) => metric.id === 'freight_margin_pct');
  const shippedVolume = topKpis.find((metric) => metric.id === 'shipped_volume');
  const projectedCases = topKpis.find((metric) => metric.id === 'projected_cases');
  const openOrderCoverage = topKpis.find((metric) => metric.id === 'open_order_coverage_pct');
  const costPerCase = topKpis.find((metric) => metric.id === 'cost_per_case');
  const distributionCostPctSales = topKpis.find((metric) => metric.id === 'distribution_cost_pct_sales');
  return (
    <div className="mt-5 space-y-4">
      <div className="rounded-md border border-[#EDE6E1] bg-white p-4">
        <div className="mb-3">
          <div>
            <div className="text-sm font-bold uppercase text-[#7A003C]">Commercial performance</div>
          </div>
        </div>
        <div className="grid gap-3 md:grid-cols-4">
          <CommercialMetricCard label="Net Sales" metric={netSales} icon={BarChart3} comparison={comparison} onShowInfo={onShowInfo} loading={loading && !metricIsLoaded(netSales)} />
          <CommercialMetricCard label="Gross Sales" metric={grossSales} icon={BarChart3} comparison={comparison} onShowInfo={onShowInfo} loading={loading && !metricIsLoaded(grossSales)} />
          <CommercialMetricCard label="Product Contribution %" metric={productContributionPct} icon={TrendingUp} comparison={comparison} onShowInfo={onShowInfo} loading={loading && !metricIsLoaded(productContributionPct)} />
          <CommercialMetricCard label="PONC" metric={ponc} icon={DollarSign} comparison={comparison} onShowInfo={onShowInfo} loading={loading && !metricIsLoaded(ponc)} />
        </div>
      </div>

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <div className="rounded-md border border-[#EDE6E1] bg-white p-4">
          <div className="text-sm font-bold uppercase text-[#7A003C]">Revenue / contribution bridge</div>
          <div className="text-xs text-[#7B6B72]">Available from the validated dashboard bridge source.</div>
          <SimpleWaterfall rows={bridgeRows} chart={bridgeChart} onShowInfo={onShowChartInfo} loading={loading && bridgeRows.length === 0} />
        </div>
        <div className="rounded-md border border-[#EDE6E1] bg-white p-4">
          <div className="text-sm font-bold uppercase text-[#7A003C]">Commercial KPIs</div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <CommercialMetricCard label="Product Contribution $" metric={productContributionDollars} icon={DollarSign} comparison={comparison} onShowInfo={onShowInfo} loading={loading && !metricIsLoaded(productContributionDollars)} />
            <CommercialMetricCard label="Trade Spend" metric={tradeSpend} icon={DollarSign} comparison={comparison} onShowInfo={onShowInfo} loading={loading && !metricIsLoaded(tradeSpend)} />
            <CommercialMetricCard label="Freight Revenue" metric={freightRevenue} icon={TrendingUp} comparison={comparison} onShowInfo={onShowInfo} loading={loading && !metricIsLoaded(freightRevenue)} />
            <CommercialMetricCard label="Freight Margin" metric={freightMargin} icon={TrendingUp} comparison={comparison} onShowInfo={onShowInfo} loading={loading && !metricIsLoaded(freightMargin)} />
            <CommercialMetricCard label="Shipped Volume" metric={shippedVolume} icon={Factory} comparison={comparison} onShowInfo={onShowInfo} loading={loading && !metricIsLoaded(shippedVolume)} />
            <CommercialMetricCard label="Projected Cases" metric={projectedCases} icon={Factory} comparison={comparison} onShowInfo={onShowInfo} loading={loading && !metricIsLoaded(projectedCases)} />
            <CommercialMetricCard label="Open-Order Coverage" metric={openOrderCoverage} icon={TrendingUp} comparison={comparison} onShowInfo={onShowInfo} loading={loading && !metricIsLoaded(openOrderCoverage)} />
            <CommercialMetricCard label="Cost per Case" metric={costPerCase} icon={DollarSign} comparison={comparison} onShowInfo={onShowInfo} loading={loading && !metricIsLoaded(costPerCase)} />
            <CommercialMetricCard label="Distribution Cost % of Sales" metric={distributionCostPctSales} icon={TrendingUp} comparison={comparison} onShowInfo={onShowInfo} loading={loading && !metricIsLoaded(distributionCostPctSales)} />
            <CommercialMetricCard label="Projected Gross-to-Net Revenue" icon={TrendingUp} comparison={comparison} />
            <CommercialMetricCard label="Quantity Sold" icon={Factory} comparison={comparison} />
            <CommercialMetricCard label="Current KPIs: 3, 6 & trailing 12 months" icon={TrendingUp} comparison={comparison} />
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <CommercialRankTable title="Top 5 Customers by Product Contribution Variance" rows={customerRows} dimension="customer" measure="contribution" rank="top" info={customerInfo} onShowInfo={onShowChartInfo} comparison={comparison} loading={loading && customerRows.length === 0} />
        <CommercialRankTable title="Bottom 5 Customers by Product Contribution Variance" rows={customerRows} dimension="customer" measure="contribution" rank="bottom" info={customerInfo} onShowInfo={onShowChartInfo} comparison={comparison} loading={loading && customerRows.length === 0} />
        <CommercialRankTable title="Top 5 Customers by Units Variance" rows={customerUnitRows} dimension="customer" measure="units" rank="top" info={customerUnitInfo} onShowInfo={onShowChartInfo} comparison={comparison} loading={loading && customerUnitRows.length === 0} />
        <CommercialRankTable title="Bottom 5 Customers by Units Variance" rows={customerUnitRows} dimension="customer" measure="units" rank="bottom" info={customerUnitInfo} onShowInfo={onShowChartInfo} comparison={comparison} loading={loading && customerUnitRows.length === 0} />
        <CommercialRankTable title="Top 5 Products by Units Variance" rows={productUnitRows} dimension="product" measure="units" rank="top" info={productUnitInfo} onShowInfo={onShowChartInfo} comparison={comparison} loading={loading && productUnitRows.length === 0} />
        <CommercialRankTable title="Bottom 5 Products by Units Variance" rows={productUnitRows} dimension="product" measure="units" rank="bottom" info={productUnitInfo} onShowInfo={onShowChartInfo} comparison={comparison} loading={loading && productUnitRows.length === 0} />
        <CommercialRankTable title="Top 5 Products by Product Contribution Variance" rows={productRows} dimension="product" measure="contribution" rank="top" info={productInfo} onShowInfo={onShowChartInfo} comparison={comparison} loading={loading && productRows.length === 0} />
        <CommercialRankTable title="Bottom 5 Products by Product Contribution Variance" rows={productRows} dimension="product" measure="contribution" rank="bottom" info={productInfo} onShowInfo={onShowChartInfo} comparison={comparison} loading={loading && productRows.length === 0} />
        <CommercialGrowthTable rows={growthRows} info={growthInfo} onShowInfo={onShowChartInfo} loading={loading && growthRows.length === 0} />
        <CommercialRankTable title="Product Class by Units Variance" rows={categoryUnitRows} dimension="category" measure="units" rank="top" allRows info={categoryUnitInfo} onShowInfo={onShowChartInfo} comparison={comparison} loading={loading && categoryUnitRows.length === 0} />
        <CommercialRankTable title="Product Class by Product Contribution Variance" rows={categoryRows} dimension="category" measure="contribution" rank="top" allRows info={categoryInfo} onShowInfo={onShowChartInfo} comparison={comparison} loading={loading && categoryRows.length === 0} />
        <CommercialTable title="Orders OTIF by customer, region and category" unavailable />
        <CommercialTable title="Top 5 customers by PONC contribution / quality cost" unavailable />
      </div>
    </div>
  );
};

const metricValue = (metric: Metric | undefined, fallback: string) => (
  metric ? formatMetricValue(metric) : fallback
);

const metricsById = (metrics: Metric[]) => (
  new Map(metrics.map((metric) => [metric.id, metric]))
);

const OperationMiniMetric = ({
  metric,
  fallbackLabel,
  comparison,
  loading,
  onShowInfo,
}: {
  metric?: Metric;
  fallbackLabel: string;
  comparison: string;
  loading: boolean;
  onShowInfo?: (metric: Metric) => void;
}) => (
  <MiniMetric
    label={metric?.name || fallbackLabel}
    note={metric?.scope_note}
    value={metricValue(metric, '--')}
    delta={formatDelta(metric, comparison)}
    good={isPositiveDelta(metric, comparison)}
    infoMetric={metricIsComingSoon(metric) || !metricIsLoaded(metric) ? undefined : metric}
    onShowInfo={onShowInfo}
    comingSoon={metricIsComingSoon(metric) && !loading}
    loading={loading && !metricIsLoaded(metric) && !metricIsComingSoon(metric)}
  />
);

const formatOperationCell = (column: string, value: unknown, currency?: string) => {
  if (value === null || value === undefined || value === '') return '--';
  const numeric = Number(value);
  const columnLower = column.toLowerCase();
  if (Number.isFinite(numeric)) {
    if (columnLower.includes('pct') || columnLower.endsWith('_percent')) return `${numeric.toFixed(1)}%`;
    if (columnLower.includes('usd') && currency && isLocalCurrencyUnit(currency)) return compactLocalCurrency(numeric, currency);
    if (columnLower.includes('usd') || columnLower.includes('contri')) return compactCurrency(numeric);
    if (columnLower.includes('qty') || columnLower.includes('units')) return numeric.toLocaleString(undefined, { maximumFractionDigits: 0 });
    return numeric.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return String(value);
};

const OperationDataTable = ({
  title,
  rows,
  columns,
  info,
  onShowInfo,
  headerAction,
  loading = false,
  maxRows = 5,
  currency,
}: {
  title: string;
  rows: any[];
  columns: string[];
  info?: ChartData;
  onShowInfo?: (chart: ChartData) => void;
  headerAction?: ReactNode;
  loading?: boolean;
  maxRows?: number;
  currency?: string;
}) => {
  const visibleColumns = columns.filter((column) => (
    rows.some((row) => row?.[column] !== undefined && row?.[column] !== null && row?.[column] !== '')
  )).slice(0, 5);
  const displayColumns = visibleColumns.length ? visibleColumns : columns.slice(0, 4);
  const tableMinWidthClass = displayColumns.length <= 2 ? 'min-w-full' : 'min-w-[28rem]';
  return (
    <div className="rounded-md border border-[#EDE6E1] bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <KpiLabel label={title} showInfo={Boolean(info)} onShowInfo={info ? () => onShowInfo?.(info) : undefined} className="text-sm font-bold uppercase text-[#7A003C]" />
        {headerAction}
      </div>
      <div className="mt-3 overflow-x-auto">
        <table className={`w-full ${tableMinWidthClass} text-left text-xs`}>
          <thead className="text-[10px] uppercase text-[#7B6B72]">
            <tr>
              {displayColumns.map((column, index) => (
                <th key={column} className={`py-1 ${index === 0 ? '' : 'text-right'}`}>
                  {(currency && isLocalCurrencyUnit(currency) ? column.replace(/_usd$/i, `_${currency}`) : column).replace(/_/g, ' ')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="font-semibold text-[#2B2027]">
            {rows.length === 0 ? (
              <tr className="border-t border-[#F1ECE8]">
                <td className="py-3 text-[#7B6B72]" colSpan={displayColumns.length}>
                  {loading ? 'Loading data…' : 'Unavailable until the configured tool returns rows for this view.'}
                </td>
              </tr>
            ) : rows.slice(0, maxRows).map((row, index) => (
              <tr key={`${title}-${index}`} className="border-t border-[#F1ECE8]">
                {displayColumns.map((column, columnIndex) => (
                  <td key={column} className={`py-1.5 ${columnIndex === 0 ? '' : 'text-right'}`}>
                    {formatOperationCell(column, row?.[column], currency)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const OperationBarChart = ({
  title,
  rows,
  valueKey,
  info,
  onShowInfo,
  loading = false,
  maxRows = 5,
}: {
  title: string;
  rows: any[];
  valueKey: string;
  info?: ChartData;
  onShowInfo?: (chart: ChartData) => void;
  loading?: boolean;
  maxRows?: number;
}) => {
  const grouped = new Map<string, { label: string; value: number }>();
  for (const row of rows) {
    const value = Number(row?.[valueKey]);
    if (!Number.isFinite(value)) continue;
    const label = operationDisplayLabel(row);
    const key = canonicalLabelKey(label);
    const existing = grouped.get(key) || { label, value: 0 };
    if (existing.label === 'Unassigned' && label !== 'Unassigned') {
      existing.label = label;
    }
    existing.value += value;
    grouped.set(key, existing);
  }
  const data = Array.from(grouped.values())
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
    .slice(0, maxRows);
  const maxAbs = Math.max(1, ...data.map((row) => Math.abs(row.value)));

  return (
    <div className="rounded-md border border-[#EDE6E1] bg-white p-4">
      <KpiLabel label={title} showInfo={Boolean(info)} onShowInfo={info ? () => onShowInfo?.(info) : undefined} className="text-sm font-bold uppercase text-[#7A003C]" />
      <div className="mt-3 space-y-2">
        {data.length === 0 ? (
          <div className="rounded-md border border-dashed border-[#E6DCD6] p-4 text-xs font-semibold text-[#7B6B72]">
            {loading ? 'Loading chart data…' : 'Chart unavailable until the configured tool returns rows.'}
          </div>
        ) : data.map((row) => {
          const width = Math.max(4, Math.abs(row.value) / maxAbs * 100);
          const isPositive = row.value >= 0;
          return (
            <div key={`${title}-${row.label}`} className="grid grid-cols-[minmax(7rem,0.9fr)_minmax(10rem,1.4fr)_5.5rem] items-center gap-3 text-xs">
              <div className="truncate font-semibold text-[#2B2027]" title={row.label}>{row.label}</div>
              <div className="h-5 rounded bg-[#F6F0ED]">
                <div
                  className={`h-5 rounded ${isPositive ? 'bg-[#0E8F5A]' : 'bg-[#C62843]'}`}
                  style={{ width: `${width}%` }}
                />
              </div>
              <div className={`text-right font-bold ${isPositive ? 'text-[#0E8F5A]' : 'text-[#C62843]'}`}>
                {formatOperationCell(valueKey, row.value)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const OperationLineTrendChart = ({
  title,
  rows,
  xKey,
  seriesKey,
  yKey,
  loading = false,
  maxSeries = 6,
}: {
  title: string;
  rows: any[];
  xKey: string;
  seriesKey: string;
  yKey: string;
  loading?: boolean;
  maxSeries?: number;
}) => {
  const [activePoint, setActivePoint] = useState<{
    label: string;
    x: string;
    value: number;
    leftPct: number;
    topPct: number;
    color: string;
    align: 'left' | 'right';
  } | null>(null);
  const validRows = rows.filter((row) => {
    const value = Number(row?.[yKey]);
    return row?.[xKey] !== undefined
      && row?.[xKey] !== null
      && row?.[seriesKey] !== undefined
      && row?.[seriesKey] !== null
      && Number.isFinite(value);
  });
  const xLabels = Array.from(new Set(validRows.map((row) => String(row[xKey])))).sort((a, b) => a.localeCompare(b));
  const xLabelName = xKey === 'Fiscal_Period' ? 'Fiscal Period' : xKey.replace(/_/g, ' ');
  const seriesLabelName = seriesKey === 'Region' ? 'Region Code' : seriesKey.replace(/_/g, ' ');
  const yLabelName = yKey === 'OTIF_Pct' ? 'OTIF %' : yKey.replace(/_/g, ' ');
  const seriesMap = new Map<string, Array<{ x: string; value: number }>>();
  for (const row of validRows) {
    const label = String(row[seriesKey] || 'Unassigned');
    const values = seriesMap.get(label) || [];
    values.push({ x: String(row[xKey]), value: Number(row[yKey]) });
    seriesMap.set(label, values);
  }
  const series = Array.from(seriesMap.entries())
    .map(([label, values]) => ({
      label,
      values,
      average: values.reduce((sum, point) => sum + point.value, 0) / Math.max(values.length, 1),
    }))
    .sort((a, b) => b.average - a.average)
    .slice(0, maxSeries);
  const allValues = series.flatMap((item) => item.values.map((point) => point.value));
  const minValue = Math.min(...allValues, 0);
  const maxValue = Math.max(...allValues, 100);
  const pad = Math.max((maxValue - minValue) * 0.08, 1);
  const yMin = Math.max(0, minValue - pad);
  const yMax = Math.min(100, maxValue + pad);
  const range = Math.max(yMax - yMin, 1);
  const width = 720;
  const height = 320;
  const left = 44;
  const right = 18;
  const top = 22;
  const bottom = 48;
  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;
  const colors = ['#7A003C', '#F47B20', '#0E8F5A', '#2563EB', '#6E3450', '#64748B'];
  const xFor = (label: string) => {
    const index = Math.max(0, xLabels.indexOf(label));
    if (xLabels.length <= 1) return left + plotWidth / 2;
    return left + (index / (xLabels.length - 1)) * plotWidth;
  };
  const yFor = (value: number) => top + ((yMax - value) / range) * plotHeight;

  return (
    <div className="rounded-md border border-[#EDE6E1] bg-white p-4">
      <h4 className="text-sm font-bold uppercase text-[#7A003C]">{title}</h4>
      {series.length === 0 ? (
        <div className="mt-3 rounded-md border border-dashed border-[#E6DCD6] p-4 text-xs font-semibold text-[#7B6B72]">
          {loading ? 'Loading trend data…' : 'Trend unavailable until the configured tool returns rows.'}
        </div>
      ) : (
        <>
          <div className="relative mt-3">
            <div className="overflow-x-auto">
              <svg viewBox={`0 0 ${width} ${height}`} className="h-[320px] min-w-[42rem]" role="img" aria-label={title}>
                <line x1={left} x2={width - right} y1={top + plotHeight} y2={top + plotHeight} stroke="#D8CEC8" />
                <line x1={left} x2={left} y1={top} y2={top + plotHeight} stroke="#D8CEC8" />
                {[0, 0.5, 1].map((ratio) => {
                  const value = yMax - range * ratio;
                  const y = top + plotHeight * ratio;
                  return (
                    <g key={ratio}>
                      <line x1={left} x2={width - right} y1={y} y2={y} stroke="#F1ECE8" />
                      <text x={left - 8} y={y + 4} textAnchor="end" className="fill-[#7B6B72] text-[10px] font-semibold">
                        {value.toFixed(0)}%
                      </text>
                    </g>
                  );
                })}
                {xLabels.map((label, index) => (
                  <text
                    key={label}
                    x={xFor(label)}
                    y={height - 20}
                    textAnchor={index === 0 ? 'start' : index === xLabels.length - 1 ? 'end' : 'middle'}
                    className="fill-[#7B6B72] text-[10px] font-semibold"
                  >
                    {label}
                  </text>
                ))}
                {series.map((item, index) => {
                  const color = colors[index % colors.length];
                  const points = item.values
                    .sort((a, b) => xLabels.indexOf(a.x) - xLabels.indexOf(b.x))
                    .map((point) => `${xFor(point.x)},${yFor(point.value)}`)
                    .join(' ');
                  return (
                    <g key={item.label}>
                      {item.values.length > 1 && (
                        <polyline points={points} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                      )}
                      {item.values.map((point) => {
                        const pointX = xFor(point.x);
                        const pointY = yFor(point.value);
                        return (
                          <circle
                            key={`${item.label}-${point.x}`}
                            cx={pointX}
                            cy={pointY}
                            r="4.5"
                            fill={color}
                            className="cursor-crosshair outline-none"
                            tabIndex={0}
                            onMouseEnter={() => setActivePoint({
                              label: item.label,
                              x: point.x,
                              value: point.value,
                              leftPct: (pointX / width) * 100,
                              topPct: (pointY / height) * 100,
                              color,
                              align: pointX > width * 0.72 ? 'right' : 'left',
                            })}
                            onFocus={() => setActivePoint({
                              label: item.label,
                              x: point.x,
                              value: point.value,
                              leftPct: (pointX / width) * 100,
                              topPct: (pointY / height) * 100,
                              color,
                              align: pointX > width * 0.72 ? 'right' : 'left',
                            })}
                            onMouseLeave={() => setActivePoint(null)}
                            onBlur={() => setActivePoint(null)}
                          >
                            <title>{`${seriesLabelName}: ${item.label}; ${xLabelName}: ${point.x}; ${yLabelName}: ${point.value.toFixed(1)}%`}</title>
                          </circle>
                        );
                      })}
                    </g>
                  );
                })}
              </svg>
            </div>
            {activePoint && (
              <div
                data-chart-tooltip="operation-line-trend"
                className={`pointer-events-none absolute z-10 min-w-[11rem] rounded-md bg-[#33252D] px-3 py-2 text-xs font-semibold text-white shadow-lg ${activePoint.align === 'right' ? '-translate-x-full' : 'translate-x-3'} -translate-y-full`}
                style={{ left: `${activePoint.leftPct}%`, top: `${activePoint.topPct}%` }}
              >
                <div className="mb-1 flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: activePoint.color }} />
                  <span>{seriesLabelName}: {activePoint.label}</span>
                </div>
                <div className="text-[#EBDDE4]">{xLabelName}: {activePoint.x}</div>
                <div className="text-[#EBDDE4]">{yLabelName}: {activePoint.value.toFixed(1)}%</div>
              </div>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-semibold text-[#4B3C44]">
            <span className="text-[#7B6B72]">{seriesLabelName}</span>
            {series.map((item, index) => (
              <span key={item.label} className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: colors[index % colors.length] }} />
                {item.label}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const RankingModeDropdown = ({
  value,
  onChange,
}: {
  value: RankingMode;
  onChange: (value: RankingMode) => void;
}) => (
  <label className="relative inline-flex items-center">
    <span className="sr-only">Ranking sort order</span>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value as RankingMode)}
      className="h-9 appearance-none rounded-md border-2 border-[#9A2E6A] bg-white py-1.5 pl-3 pr-9 text-sm font-semibold text-[#7B6B72] outline-none transition-colors hover:border-[#7A003C] focus:border-[#7A003C] focus:ring-2 focus:ring-[#7A003C]/15"
    >
      <option value="top">High to low</option>
      <option value="bottom">Low to high</option>
    </select>
    <ChevronDown className="pointer-events-none absolute right-3 h-4 w-4 text-[#7B6B72]" />
  </label>
);

const formatLineageValue = (value: unknown, metric?: Metric) => {
  if (value === null || value === undefined || value === '') return '--';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  if (metric?.unit === 'USD') return compactCurrency(numeric);
  if (isLocalCurrencyUnit(metric?.unit)) return compactLocalCurrency(numeric, String(metric?.unit));
  if (metric?.unit === '%') return `${numeric.toFixed(1)}%`;
  if (Math.abs(numeric) >= 1_000_000) return numeric.toLocaleString(undefined, { maximumFractionDigits: 1 });
  return numeric.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const CodeChip = ({ children }: { children: ReactNode }) => (
  <span className="rounded-md border border-[#E7DAD3] bg-[#FAF7F1] px-1.5 py-0.5 font-mono text-[10px] font-semibold text-[#6E3450]">
    {children}
  </span>
);

const MetricLineageDrawer = ({ metric, onClose }: { metric: Metric | null; onClose: () => void }) => {
  if (!metric) return null;
  const lineage = metric.lineage_v2;
  const values = lineage?.values || [];
  const refs = lineage?.odata_refs || [];
  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Close data lineage"
        className="absolute inset-0 cursor-default bg-[#1F1A20]/35 backdrop-blur-[1px]"
        onClick={onClose}
      />
      <aside className="absolute right-0 top-0 h-full w-full max-w-[31rem] overflow-y-auto bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#9A2E6A]">Data Lineage</div>
            <h2 className="mt-2 text-xl font-bold text-[#1F1A20]">{metric.name}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close data lineage"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#7B6B72] transition-colors hover:bg-[#FAF7F1] hover:text-[#7A003C]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {(lineage?.what_it_is || metric.methodology) && (
          <section className="mt-7">
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#7B6B72]">Definition / Calculation</div>
            <p className="mt-2 rounded-md border border-[#EDE6E1] bg-[#FAF7F1] p-3 text-sm leading-6 text-[#2B2027]">
              {lineage?.what_it_is || metric.methodology}
            </p>
          </section>
        )}

        <section className="mt-7">
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#7B6B72]">Values ({Math.max(values.length, metric.value === null || metric.value === undefined ? 0 : 1)})</div>
          <div className="mt-3 overflow-hidden rounded-md border border-[#EDE6E1]">
            {(values.length ? values : [{
              slot: 'current',
              label: 'Current',
              value_raw: metric.value,
              column: metric.source_field || metric.id,
              column_label: metric.name,
              source: 'tool',
              odata_ref: null,
            }]).map((value, index) => (
              <div key={`${value.slot}-${index}`} className="border-t border-[#F1ECE8] p-3 first:border-t-0">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-[#7B6B72]">
                    <ChevronRight className="h-3.5 w-3.5" />
                    {value.label}
                  </div>
                  <div className="text-sm font-bold text-[#1F1A20]">{formatLineageValue(value.value_raw, metric)}</div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-[#7B6B72]">
                  <CodeChip>{value.column || metric.source_field || metric.id}</CodeChip>
                  <span className="rounded-md border border-[#E7DAD3] bg-white px-1.5 py-0.5 text-[10px] font-bold uppercase text-[#9A2E6A]">{value.source || 'tool'}</span>
                  <span>{value.column_label || metric.name}</span>
                </div>
                {value.derivation?.formula && (
                  <div className="mt-2 rounded-md bg-[#FAF7F1] p-2 font-mono text-[11px] text-[#4B3C44]">
                    {value.derivation.formula_pretty || value.derivation.formula}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="mt-7">
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#7B6B72]">OData References ({refs.length})</div>
          <div className="mt-3 space-y-3">
            {refs.length === 0 ? (
              <div className="rounded-md border border-dashed border-[#E6DCD6] p-3 text-sm font-semibold text-[#7B6B72]">
                Source call details are unavailable for this metric.
              </div>
            ) : refs.map((ref) => (
              <div key={ref.id} className="rounded-md border border-[#EDE6E1] p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-sm font-bold text-[#2B2027]">
                    <Database className="h-4 w-4 text-[#7A003C]" />
                    <CodeChip>{ref.id}</CodeChip>
                  </div>
                  <span className="text-xs font-semibold text-[#7B6B72]">{ref.row_count ?? '--'} rows</span>
                </div>
                <div className="mt-2 text-xs text-[#7B6B72]">{ref.model_name || 'Tool source'}</div>
                {ref.select && ref.select.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {ref.select.slice(0, 10).map((column) => <CodeChip key={column}>{column}</CodeChip>)}
                  </div>
                )}
                {ref.filter && <div className="mt-2 break-words text-xs text-[#7B6B72]">Filter: {ref.filter}</div>}
              </div>
            ))}
          </div>
        </section>
      </aside>
    </div>
  );
};

const selectedControlLabel = (value: string, options: SmartMorningControlOption[]) => (
  options.find((option) => option.value === value)?.label || value
);

const HeaderPillDropdown = ({
  label,
  options,
  onChange,
  disabled,
}: {
  label: string;
  options: SmartMorningControlOption[];
  onChange?: (value: string) => void;
  disabled?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const enabled = Boolean(onChange && options.length > 0 && !disabled);
  const dropdownRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (event.target instanceof Node && !dropdownRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  if (!enabled) {
    return <span className="rounded-md bg-[#7A003C] px-4 py-2 text-white">{label}</span>;
  }

  return (
    <span ref={dropdownRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="rounded-md bg-[#7A003C] px-4 py-2 text-white"
      >
        {label}
      </button>
      {open && (
        <span className="absolute right-0 top-full z-50 mt-1 min-w-full overflow-hidden rounded-md border border-[#EDE6E1] bg-white py-1 text-left text-xs font-semibold text-[#2B2027] shadow-lg">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                setOpen(false);
                onChange?.(option.value);
              }}
              className="block w-full whitespace-nowrap px-4 py-2 text-left hover:bg-[#FAF7F1]"
            >
              {option.label}
            </button>
          ))}
        </span>
      )}
    </span>
  );
};

export default function SmartMorningLeadersDashboard({
  response,
  periodOptions = [],
  comparisonOptions = [],
  onPeriodChange,
  onComparisonChange,
  controlsDisabled = false,
  loading = false,
  onForceRefresh,
  forceRefreshDisabled = false,
  initialTab = 'finance',
  onTabChange,
  activeTab: controlledActiveTab,
  onFinanceClick,
  onCommercialClick,
  onOperationsClick,
  onArClick,
  contentOverride,
  arFilterControls,
}: SmartMorningLeadersDashboardProps) {
  const [internalActiveTab, setInternalActiveTab] = useState<'finance' | 'commercial' | 'operations' | 'ar'>(initialTab);
  const activeTab = controlledActiveTab ?? internalActiveTab;
  const [lineageContext, setLineageContext] = useState<LineageContext | null>(null);
  const [customerContributionMode, setCustomerContributionMode] = useState<RankingMode>('top');
  const [productContributionMode, setProductContributionMode] = useState<RankingMode>('top');
  const [refreshConfirmOpen, setRefreshConfirmOpen] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const handleTabChange = (tab: 'finance' | 'commercial' | 'operations' | 'ar') => {
    setInternalActiveTab(tab);
    onTabChange?.(tab);
    if (tab === 'finance') onFinanceClick?.();
    if (tab === 'commercial') onCommercialClick?.();
    if (tab === 'operations') onOperationsClick?.();
    if (tab === 'ar') onArClick?.();
  };
  const blocks = asBlocks(response);
  const meta = asRecord(response.meta);
  const parameters = asRecord(meta.parameters);
  const templateId = String(meta.template_id || `smart_morning_${activeTab}`);
  const activeDashboardId = activeTab as SmartMorningDashboardId;
  const activeDashboardLabel = activeDashboardId.charAt(0).toUpperCase() + activeDashboardId.slice(1);
  const topKpis = (blockById(blocks, 'top_kpis')?.data?.items || blockById(blocks, 'commercial_kpis')?.data?.items || []) as Metric[];
  const bridgeRows = blockById(blocks, 'net_income_growth')?.data?.rows || blockById(blocks, 'commercial_bridge')?.data?.rows || [];
  const bridgeBlock = blockById(blocks, 'net_income_growth') || blockById(blocks, 'commercial_bridge');
  const bridgeChart = {
    title: 'Revenue / Contribution Bridge',
    caption: 'Revenue and contribution bridge for the selected period.',
    lineage_v2: bridgeBlock?.data?.lineage_v2 || {
      what_it_is: 'Revenue / contribution bridge showing the base, price/mix, volume, and actual values for the selected period.',
      values: bridgeRows.map((row: any) => ({
        slot: String(row.driver || 'value'),
        label: String(row.driver || 'Value'),
        value_raw: row.value ?? null,
        column: 'value',
        column_label: 'Bridge value',
        source: 'tool',
        derivation: null,
        odata_ref: null,
      })),
      odata_refs: [],
    },
  } as unknown as ChartData;
  const growthRows = blockById(blocks, 'commercial_growth')?.data?.rows || [];
  const commercialCustomerRows = blockById(blocks, 'commercial_customers')?.data?.rows || [];
  const commercialCustomerUnitRows = blockById(blocks, 'commercial_customers_units')?.data?.rows || [];
  const commercialProductRows = blockById(blocks, 'commercial_products_contribution')?.data?.rows || [];
  const commercialProductUnitRows = blockById(blocks, 'commercial_products_units')?.data?.rows || [];
  const commercialCategoryRows = blockById(blocks, 'commercial_categories_contribution')?.data?.rows || [];
  const commercialCategoryUnitRows = blockById(blocks, 'commercial_categories_units')?.data?.rows || [];
  const customerRows = blockById(blocks, 'top_customers')?.data?.rows || [];
  const volumeRows = blockById(blocks, 'total_volume')?.data?.rows || [];
  const channelRows = blockById(blocks, 'product_contribution_by_channel')?.data?.rows || [];
  const ops = ((blockById(blocks, 'operational_excellence')?.data?.items || []) as Metric[]);
  const supply = ((blockById(blocks, 'supply_chain_performance')?.data?.items || blockById(blocks, 'commercial_ponc')?.data?.items || []) as Metric[]);
  const currentDirectLaborProductivity = topKpis.find((metric) => metric.id === 'direct_labor_productivity');
  const directLaborProductivity = currentDirectLaborProductivity;
  const currentFinanceYield = ops.find((metric) => metric.id === 'yield_pct');
  const financeYield = currentFinanceYield;
  const operationOverview = ((blockById(blocks, 'commercial_kpis')?.data?.items || []) as Metric[]);
  const operationCostToServe = ((blockById(blocks, 'cost_to_serve')?.data?.items || []) as Metric[]);
  const operationSalesMargin = ((blockById(blocks, 'sales_margin')?.data?.items || []) as Metric[]);
  const operationSalesProjection = ((blockById(blocks, 'sales_projection')?.data?.items || []) as Metric[]);
  const operationSupplyChain = ((blockById(blocks, 'supply_chain_performance')?.data?.items || []) as Metric[]);
  const operationOtifPlantTopRows = blockById(blocks, 'otif_by_plant_top')?.data?.rows || [];
  const operationOtifPlantBottomRows = blockById(blocks, 'otif_by_plant_bottom')?.data?.rows || [];
  const operationOtifCustomerRows = blockById(blocks, 'otif_by_customer_table')?.data?.rows || [];
  const operationOtifRegionTrendRows = blockById(blocks, 'otif_region_trend')?.data?.rows || [];
  const operationTopCustomers = blockById(blocks, 'top_customers_by_contribution')?.data?.rows || [];
  const operationCustomerRows = operationTopCustomers.map((row: Record<string, any>) => ({
    ...row,
    Customer: operationDisplayLabel(row, [
      ['Customer'],
      ['End_Customer_End_Customer', 'customer', 'customer_name', 'Sold_To_Party_T', 'Sold_To_Party', 'End_Customer'],
    ]),
  }));
  const operationTopProducts = blockById(blocks, 'top_products_by_contribution')?.data?.rows || [];
  const operationTopProductRows = operationTopProducts.map((row: Record<string, any>) => ({
    ...row,
    Product: operationDisplayLabel(row, productLabelGroups),
  }));
  const displayedCustomerRows = rankedContributionRows(operationCustomerRows, customerContributionMode);
  const displayedProductRows = rankedContributionRows(operationTopProductRows, productContributionMode);
  const operationCategoryRows = blockById(blocks, 'category_by_contribution')?.data?.rows || [];
  const operationGrowthRows = blockById(blocks, 'growth_rankings')?.data?.rows || [];
  const operationDeclineRows = blockById(blocks, 'decline_rankings')?.data?.rows || [];
  const operationGrowthRankRows = aggregateRankingRows(operationGrowthRows, 'growth');
  const operationDeclineRankRows = aggregateRankingRows(operationDeclineRows, 'decline');
  const operationMetricLookup = metricsById([
    ...operationOverview,
    ...operationCostToServe,
    ...ops,
    ...operationSalesMargin,
    ...operationSalesProjection,
    ...operationSupplyChain,
  ]);
  const insights = blockById(blocks, 'executive_insights')?.data?.rows || [];
  const comparison = String(parameters.comparison || 'Last Year');
  const periodLabel = String(parameters.period_label || 'YTD FY2026 (Jun 2025)');
  const periodControlLabel = selectedControlLabel(String(parameters.tool_period || periodLabel), periodOptions);
  const periodMenuOptions = periodOptions.filter((option) => {
    if (option.value !== 'YTD') return true;
    return !/P1-P\d+/i.test(periodControlLabel);
  });
  const comparisonControlLabel = selectedControlLabel(comparison, comparisonOptions);
  const lastUpdated = formatLastUpdated(
    meta.last_updated_at
      ?? meta.lastUpdatedAt
      ?? parameters.last_updated
      ?? parameters.lastUpdated,
  );
  const isStaleFallback = String(meta.data_freshness || '').toLowerCase() === 'stale';
  const volumeValue = formatVolume(volumeRows);
  const volumeComparison = formatVolumeComparison(volumeRows, comparison);
  const volumeLoading = loading && volumeRows.length === 0;
  const showMetricInfo = (metric: Metric) => setLineageContext({ kind: 'kpi', metric: { ...metric, label: metric.name } as unknown as KPIMetric });
  const showChartInfo = (chart: ChartData) => setLineageContext({ kind: 'chart', chart });
  const blockInfo = (id: string, title: string): ChartData => ({
    type: 'matrix',
    title,
    data: [],
    lineage_v2: blockById(blocks, id)?.data?.lineage_v2,
  } as ChartData);
  const customerInfo = blockInfo('top_customers', 'Top 5 Customers by Product Contribution');
  const channelInfo = blockInfo('product_contribution_by_channel', 'Product Contribution by Channel');
  const totalVolumeInfo = blockInfo('total_volume', 'Total Volume');
  const commercialCustomerInfo = blockInfo('commercial_customers', 'Customers by Product Contribution');
  const commercialCustomerUnitInfo = blockInfo('commercial_customers_units', 'Customers by Units');
  const commercialProductInfo = blockInfo('commercial_products_contribution', 'Products by Product Contribution');
  const commercialProductUnitInfo = blockInfo('commercial_products_units', 'Products by Units');
  const commercialCategoryInfo = blockInfo('commercial_categories_contribution', 'Product Class by Product Contribution');
  const commercialCategoryUnitInfo = blockInfo('commercial_categories_units', 'Product Class by Units');
  const commercialGrowthInfo = blockInfo('commercial_growth', 'Ranking by Channel');
  const operationMetric = (id: string) => {
    const metric = operationMetricLookup.get(id);
    return metric;
  };
  const operationMini = (id: string, fallbackLabel: string) => (
    <OperationMiniMetric
      metric={operationMetric(id)}
      fallbackLabel={fallbackLabel}
      comparison={comparison}
      loading={loading}
      onShowInfo={showMetricInfo}
    />
  );
const arKpis = ((blockById(blocks, 'ar_kpis')?.data?.items || []) as Metric[]);
 const arCaptions = ((blockById(blocks, 'ar_kpi_captions')?.data?.items || []) as Metric[]);
 const arMetricLookup = metricsById([...arKpis, ...arCaptions]);
 const arMetric = (id: string) => arMetricLookup.get(id);
 const arCurrency = String(arMetric('total_ar_usd')?.unit || 'USD');
 const arCaption = (id: string, suffix: string) => {
   const metric = arMetric(id);
   if (!metric || metric.value === null || metric.value === undefined) return undefined;
   return `${formatMetricValue(metric)} ${suffix}`.trim();
 };
 const arMini = (id: string, fallbackLabel: string) => (
<OperationMiniMetric
     metric={arMetric(id)}
     fallbackLabel={fallbackLabel}
     comparison={comparison}
     loading={loading}
     onShowInfo={showMetricInfo}
   />
 );
 const arTargetDays = (blockById(blocks, 'ar_dso_trend')?.data as any)?.sla_threshold_days ?? 23;
 const arAgingRows = blockById(blocks, 'ar_aging_by_bucket')?.data?.rows || [];
 const arTopPastDueRows = blockById(blocks, 'ar_top_past_due')?.data?.rows || [];
 const arSlaRows = blockById(blocks, 'ar_deductions_sla')?.data?.rows || [];
 const arDsoTrendRows = (blockById(blocks, 'ar_dso_trend')?.data?.rows || [])
   .map((row: Record<string, any>) => ({ ...row, Series: 'DSO' }));
 const arBody = (
<>
<div className="mt-5 grid gap-0 overflow-hidden rounded-md border border-[#EDE6E1] lg:grid-cols-3">
<MetricCard
         metric={arMetric('total_ar_usd')}
         icon={DollarSign}
         comparison={comparison}
         onShowInfo={showMetricInfo}
         caption={arCaption('open_invoice_count', 'open invoices')}
         loading={loading && !metricIsLoaded(arMetric('total_ar_usd'))}
       />
<MetricCard
         metric={arMetric('dso_days')}
         icon={Calendar}
         comparison={comparison}
         onShowInfo={showMetricInfo}
         caption={`target ${arTargetDays} days`}
         loading={loading && !metricIsLoaded(arMetric('dso_days'))}
       />
<MetricCard
         metric={arMetric('current_ar_pct')}
         icon={BarChart3}
         comparison={comparison}
         onShowInfo={showMetricInfo}
         caption="share of gross open AR"
         loading={loading && !metricIsLoaded(arMetric('current_ar_pct'))}
       />
<MetricCard
         metric={arMetric('past_due_90_plus_usd')}
         icon={AlertTriangle}
         comparison={comparison}
         onShowInfo={showMetricInfo}
         caption={arCaption('past_due_90_plus_pct_of_ar', 'of AR')}
         loading={loading && !metricIsLoaded(arMetric('past_due_90_plus_usd'))}
       />
<MetricCard
         metric={arMetric('cei_pct')}
         icon={TrendingUp}
         comparison={comparison}
         onShowInfo={showMetricInfo}
         caption="collection effectiveness proxy"
         loading={loading && !metricIsLoaded(arMetric('cei_pct'))}
       />
<MetricCard
         metric={arMetric('open_deductions_usd')}
         icon={AlertTriangle}
         comparison={comparison}
         onShowInfo={showMetricInfo}
         caption={arCaption('deductions_past_sla_pct', 'past SLA')}
         comingSoon={metricIsComingSoon(arMetric('open_deductions_usd'))}
         loading={loading && !metricIsLoaded(arMetric('open_deductions_usd'))}
       />
</div>
<div className="mt-4 grid gap-3 xl:grid-cols-2">
<OperationDataTable
         title="AR Aging by Bucket"
         rows={arAgingRows}
         columns={['aging_bucket', 'open_ar_usd']}
         currency={arCurrency}
         info={blockInfo('ar_aging_by_bucket', 'AR Aging by Bucket')}
         onShowInfo={showChartInfo}
         loading={loading && arAgingRows.length === 0}
         maxRows={6}
       />
<OperationLineTrendChart
         title="DSO Trend by Fiscal Period"
         rows={arDsoTrendRows}
         xKey="_Period_Label"
         seriesKey="Series"
         yKey="monthly_dso_days"
         loading={loading && arDsoTrendRows.length === 0}
       />
</div>
<div className="mt-4">
<OperationDataTable
         title="Top Past-Due Accounts"
         rows={arTopPastDueRows}
         columns={['customer_name', 'gross_open_ar_usd', 'past_due_ar_usd', 'over_30_days_past_due_pct']}
         currency={arCurrency}
         info={blockInfo('ar_top_past_due', 'Top Past-Due Accounts')}
         onShowInfo={showChartInfo}
         loading={loading && arTopPastDueRows.length === 0}
         maxRows={5}
       />
</div>
<div className="mt-4 rounded-md border border-[#EDE6E1] bg-white">
<SectionHeader
         number="1"
         title="Deductions & Disputes vs. SLA"
         subtitle="Open disputed amount by case age against the SLA threshold"
         icon={ClipboardList}
       />
<div className="grid gap-3 p-4 md:grid-cols-4">
         {arMini('open_deductions_usd', 'Open $')}
         {arMini('open_claims_count', 'Open Claims')}
         {arMini('deductions_past_sla_usd', 'Past SLA Amount')}
         {arMini('deductions_past_sla_pct', '% Past SLA')}
</div>
<div className="px-4 pb-4">
<OperationDataTable
           title="Deductions & Disputes vs SLA"
           rows={arSlaRows}
           columns={['age_bucket', 'disputed_amount_usd', 'open_case_count']}
           info={blockInfo('ar_deductions_sla', 'Deductions & Disputes vs. SLA')}
           onShowInfo={showChartInfo}
           loading={loading && arSlaRows.length === 0}
           maxRows={7}
         />
</div>
</div>
</>
 );
 
  const requestForceRefresh = () => {
    if (forceRefreshDisabled) return;
    setRefreshConfirmOpen(true);
  };
  const confirmForceRefresh = () => {
    setRefreshConfirmOpen(false);
    onForceRefresh?.();
  };
  const openFeedbackModal = () => {
    setFeedbackError(null);
    setShowFeedbackModal(true);
  };
  const handleFeedbackSubmit = async (rating: number, feedback: string) => {
    if (rating !== 5 && rating !== 1) return;
    const trimmed = feedback.trim();
    if (!trimmed) return;

    setFeedbackLoading(true);
    setFeedbackError(null);
    try {
      const result = await preCannedReportsApi.submitDashboardFeedback({
        templateId,
        dashboardId: activeDashboardId,
        parameters,
        rating: rating === 5 ? 'thumbs_up' : 'thumbs_down',
        text: trimmed,
      });
      if (result.status === 'submitted') {
        setShowFeedbackModal(false);
        return;
      }
      setFeedbackError('Feedback could not be submitted for this dashboard.');
    } catch {
      setFeedbackError('Feedback could not be submitted for this dashboard.');
    } finally {
      setFeedbackLoading(false);
    }
  };
  const operationsBody = (
    <>
      <div className="mt-5 grid gap-0 overflow-hidden rounded-md border border-[#EDE6E1] lg:grid-cols-4">
        <MetricCard metric={operationMetric('net_sales_usd')} icon={DollarSign} comparison={comparison} onShowInfo={showMetricInfo} loading={loading && !metricIsLoaded(operationMetric('net_sales_usd'))} />
        <MetricCard metric={operationMetric('gross_sales_usd')} icon={BarChart3} comparison={comparison} onShowInfo={showMetricInfo} loading={loading && !metricIsLoaded(operationMetric('gross_sales_usd'))} />
        <MetricCard metric={operationMetric('open_order_book_value')} icon={ClipboardList} comparison={comparison} onShowInfo={showMetricInfo} loading={loading && !metricIsLoaded(operationMetric('open_order_book_value'))} />
        <MetricCard metric={operationMetric('open_order_book_cases')} icon={Factory} comparison={comparison} onShowInfo={showMetricInfo} loading={loading && !metricIsLoaded(operationMetric('open_order_book_cases'))} />
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-2">
        <div className="rounded-md border border-[#EDE6E1] bg-white">
          <SectionHeader number="1" title="Commercial Operations" subtitle="Track revenue, margin, order book and cost-to-serve performance" icon={BarChart3} />
          <div className="p-4">
            <h3 className="text-sm font-bold uppercase text-[#7A003C]">Cost to Serve</h3>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              {operationMini('distribution_cost_pct_sales', 'Distribution Cost % of Sales')}
              {operationMini('warehouse_cost_pct_sales', 'Warehouse Cost % of Sales')}
              {operationMini('freight_cost_pct_sales', 'Freight Cost % of Sales')}
              {operationMini('cost_per_case', 'Cost per Case')}
            </div>

            <h3 className="mt-6 text-sm font-bold uppercase text-[#7A003C]">Sales Projection</h3>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              {operationMini('projected_cases', 'Projected Cases')}
              {operationMini('open_order_coverage_pct', 'Open-Order Coverage')}
              {operationMini('projected_gross_revenue', 'Projected Gross Revenue')}
              {operationMini('projected_net_revenue', 'Projected Net Revenue')}
            </div>
          </div>
        </div>

        <div className="rounded-md border border-[#EDE6E1] bg-white">
          <SectionHeader number="2" title="Consistency & Optimization" subtitle="Operate with excellence across the end-to-end value chain" icon={Factory} />
          <div className="p-4">
            <h3 className="text-sm font-bold uppercase text-[#7A003C]">Operational Excellence</h3>
            <div className="mt-3 grid gap-3 md:grid-cols-3">
              {operationMini('oee_pct', 'OEE')}
              {operationMini('yield_pct', 'Yield')}
              {operationMini('mfg_cost_per_lb', 'MFG Cost / LB')}
              {operationMini('labor_productivity', 'Labor Productivity')}
              {operationMini('muv_dollars', 'MUV')}
              {operationMini('schedule_attainment_pct', 'Schedule Attainment')}
            </div>

            <h3 className="mt-6 text-sm font-bold uppercase text-[#7A003C]">Supply Chain Performance</h3>
            <div className="mt-3 grid gap-3 md:grid-cols-3">
              {operationMini('ponc_dollars', 'PONC')}
              {operationMini('case_fill_by_plant_pct', 'OTIF / Case Fill by Plant')}
              {operationMini('finished_goods_inventory_usd', 'Finished Goods Inventory')}
              {operationMini('raw_packaging_inventory_usd', 'Raw & Packaging Inventory')}
              {operationMini('forecast_accuracy_pct', 'Forecast Accuracy')}
              {operationMini('current_kpis_rolling', 'Current KPIs by 3, 6 & T12')}
            </div>
          </div>
        </div>
      </div>

      <div data-section="operation-otif-performance" className="mt-4 rounded-md border border-[#EDE6E1] bg-white p-4">
        <h3 className="text-sm font-bold uppercase text-[#7A003C]">OTIF Performance</h3>
        <div className="mt-3 grid gap-3 xl:grid-cols-2">
          <OperationDataTable
            title="OTIF by Plant - Top 5"
            rows={operationOtifPlantTopRows}
            columns={['Plant', 'OTIF_Pct']}
            loading={loading && operationOtifPlantTopRows.length === 0}
          />
          <OperationDataTable
            title="OTIF by Plant - Bottom 5"
            rows={operationOtifPlantBottomRows}
            columns={['Plant', 'OTIF_Pct']}
            loading={loading && operationOtifPlantBottomRows.length === 0}
          />
        </div>
        <div data-section="operation-otif-detail-grid" className="mt-3 grid gap-3 xl:grid-cols-2">
          <OperationDataTable
            title="Orders OTIF by Customer - Top 10 Highest Volume"
            rows={operationOtifCustomerRows}
            columns={['Customer', 'Order_OTIF_Qty_CS', 'OTIF_Pct']}
            loading={loading && operationOtifCustomerRows.length === 0}
            maxRows={10}
          />
          <OperationLineTrendChart
            title="Trending OTIF by Region - YTD"
            rows={operationOtifRegionTrendRows}
            xKey="Fiscal_Period"
            seriesKey="Region"
            yKey="OTIF_Pct"
            loading={loading && operationOtifRegionTrendRows.length === 0}
          />
        </div>
      </div>

      <div className="mt-4 rounded-md border border-[#EDE6E1] bg-white">
        <SectionHeader number="3" title="Sales & Margin" subtitle="Monitor shipped volume, margin, trade spend and freight economics" icon={DollarSign} />
        <div className="grid gap-3 p-4 md:grid-cols-3 xl:grid-cols-6">
          {operationMini('product_contribution_usd', 'Product Contribution $')}
          {operationMini('product_contribution_pct', 'Product Contribution %')}
          {operationMini('shipped_volume_cases', 'Shipped Volume')}
          {operationMini('trade_spend_usd', 'Trade Spend')}
          {operationMini('freight_revenue_usd', 'Freight Revenue')}
          {operationMini('freight_margin_pct', 'Freight Margin')}
        </div>
      </div>

      <div className="mt-4 rounded-md border border-[#EDE6E1] bg-white">
        <SectionHeader number="4" title="Rankings & Contribution" subtitle="Compare customer, product and category contribution across the commercial operations portfolio" icon={ClipboardList} />
        <div className="grid gap-4 p-4">
          <div className="grid gap-4 xl:grid-cols-2">
            <OperationDataTable
              title={`${customerContributionMode === 'top' ? 'Top' : 'Bottom'} 5 Customers by Units & Product Contribution`}
              rows={displayedCustomerRows}
              columns={['Customer', 'Net_Sales_USD', 'Prod_Contri_USD', 'Product_Contribution_Pct']}
              info={blockInfo('top_customers_by_contribution', 'Customers by Units & Product Contribution')}
              onShowInfo={showChartInfo}
              headerAction={<RankingModeDropdown value={customerContributionMode} onChange={setCustomerContributionMode} />}
              loading={loading && operationTopCustomers.length === 0}
            />
            <OperationDataTable
              title={`${productContributionMode === 'top' ? 'Top' : 'Bottom'} 5 Products by Units & Product Contribution`}
              rows={displayedProductRows}
              columns={['Product', 'Net_Sales_USD', 'Prod_Contri_USD', 'Product_Contribution_Pct']}
              info={blockInfo('top_products_by_contribution', 'Products by Units & Product Contribution')}
              onShowInfo={showChartInfo}
              headerAction={<RankingModeDropdown value={productContributionMode} onChange={setProductContributionMode} />}
              loading={loading && operationTopProducts.length === 0}
            />
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <OperationBarChart
              title="Growth Rankings"
              rows={operationGrowthRankRows}
              valueKey="Net_Sales_USD_vs_LY_USD"
              info={blockInfo('growth_rankings', 'Growth Rankings')}
              onShowInfo={showChartInfo}
              loading={loading && operationGrowthRows.length === 0}
            />
            <OperationBarChart
              title="Decline Rankings"
              rows={operationDeclineRankRows}
              valueKey="Net_Sales_USD_vs_LY_USD"
              info={blockInfo('decline_rankings', 'Decline Rankings')}
              onShowInfo={showChartInfo}
              loading={loading && operationDeclineRows.length === 0}
            />
          </div>

          <div className="grid gap-4">
            <OperationDataTable
              title="Category by Units & Product Contribution - All"
              rows={operationCategoryRows}
              columns={['Prod_Class_Mat_Sales_Comp_T', 'Net_Sales_USD', 'Prod_Contri_USD', 'Product_Contribution_Pct']}
              info={blockInfo('category_by_contribution', 'Category by Units & Product Contribution')}
              onShowInfo={showChartInfo}
              loading={loading && operationCategoryRows.length === 0}
              maxRows={8}
            />
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="relative rounded-md border border-[#E5DDD8] bg-white p-6 shadow-sm">
      <div className="sticky top-0 z-30 bg-white">
      <div className="flex flex-col gap-4 border-b border-[#EDE6E1] pb-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-3 whitespace-nowrap">
            <span className="text-4xl font-bold tracking-tight text-[#7A003C]">SmartMorning</span>
            <span className="h-10 w-px bg-[#E6DCD6]" aria-hidden="true" />
            <span className="text-3xl font-bold text-[#F47B20]">for Leaders</span>
          </div>
          <div className="mt-4">
            <div className="text-xs font-bold uppercase text-[#7A003C]">Our Aspiration</div>
            <div className="text-2xl font-bold text-[#1F1A20]">{parameters.aspiration || '$3B in Net Sales by FY2028'}</div>
          </div>
        </div>
        <div className="flex flex-col gap-3 lg:items-end">
        <div className="grid w-full gap-4 text-xs font-semibold sm:grid-cols-[minmax(0,1fr)_max-content] sm:items-end lg:w-auto lg:gap-x-8">
          {activeTab === 'ar' && arFilterControls ? (
  <div className="min-w-0">
    {arFilterControls}
  </div>
) : (
  <div className="grid gap-2">
    <div className="flex items-center gap-2 text-sm font-bold text-[#1F1A20]">
      <Calendar className="h-4 w-4 text-[#7A003C]" />
      <span>Time Period</span>
    </div>

    <div className="flex min-h-9 flex-wrap items-center gap-3">
      <HeaderPillDropdown
        label={
          periodControlLabel === String(parameters.tool_period || '')
            ? periodLabel
            : periodControlLabel
        }
        options={periodMenuOptions}
        onChange={onPeriodChange}
        disabled={controlsDisabled}
      />

      <span className="text-sm font-bold text-[#1F1A20]">
        vs
      </span>

      <HeaderPillDropdown
        label={comparisonControlLabel}
        options={comparisonOptions}
        onChange={onComparisonChange}
        disabled={controlsDisabled}
      />
    </div>
  </div>
)}
            <div className="grid gap-2 sm:justify-items-end">
              <div className="flex items-center gap-2 text-[11px] font-bold text-[#1F1A20] sm:text-right">
                <span>Last Updated</span>
              </div>
              <div className="flex min-h-9 items-center gap-1.5 whitespace-nowrap text-[11px] font-semibold text-[#2B2027] sm:justify-end">
                <span className="tabular-nums">{loading ? 'Refreshing…' : lastUpdated || '--'}</span>
                {onForceRefresh && (
                  <button
                    type="button"
                    onClick={requestForceRefresh}
                    disabled={forceRefreshDisabled}
                    aria-label="Refresh dashboard data"
                    title="Refresh dashboard data"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-[#7A003C] text-white transition-colors hover:bg-[#650032] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <RefreshCw className={`h-4 w-4 ${forceRefreshDisabled ? 'animate-spin' : ''}`} />
                  </button>
                )}
                {!loading && isStaleFallback && (
                  <span className="rounded border border-[#E8B9A6] bg-[#FFF2EC] px-1.5 py-0.5 text-[10px] font-bold uppercase text-[#A5481F]">
                    Stale
                  </span>
                )}
              </div>
              {feedbackError && (
                <div className="text-[11px] font-semibold text-[#C62843]">
                  {feedbackError}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-end justify-between border-b border-[#EDE6E1]">
        <div role="tablist" aria-label="SmartMorning report sections" className="flex items-end gap-1">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'finance'}
            onClick={() => handleTabChange('finance')}
            disabled={controlsDisabled}
            className={`border-b-2 px-5 py-3 text-sm font-semibold ${
              activeTab === 'finance'
                ? 'border-[#7A003C] font-bold text-[#7A003C]'
                : 'border-transparent text-[#7B6B72] transition-colors hover:border-[#7A003C] hover:text-[#7A003C]'
            } ${controlsDisabled ? 'cursor-not-allowed' : ''}`}
          >
            Finance
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'commercial'}
            onClick={() => handleTabChange('commercial')}
            disabled={controlsDisabled}
            className={`border-b-2 px-5 py-3 text-sm font-semibold ${
              activeTab === 'commercial'
                ? 'border-[#7A003C] font-bold text-[#7A003C]'
                : 'border-transparent text-[#7B6B72] transition-colors hover:border-[#7A003C] hover:text-[#7A003C]'
            } ${controlsDisabled ? 'cursor-not-allowed' : ''}`}
          >
            Commercial
          </button>
          <div className="group relative">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'operations'}
              onClick={() => handleTabChange('operations')}
              disabled={controlsDisabled || !onOperationsClick}
              className={`border-b-2 px-5 py-3 text-sm font-semibold ${
                activeTab === 'operations'
                  ? 'border-[#7A003C] font-bold text-[#7A003C]'
                  : 'border-transparent'
              } ${
                onOperationsClick && activeTab !== 'operations'
                  ? `text-[#7B6B72] transition-colors hover:border-[#7A003C] hover:text-[#7A003C]${controlsDisabled ? ' cursor-not-allowed' : ''}`
                  : !onOperationsClick
                    ? 'cursor-not-allowed text-[#A99CA2]'
                    : ''
              }`}
            >
              Operations
            </button>
          </div>
          <div className="group relative">

            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'ar'}
              onClick={() => handleTabChange('ar')}
              disabled={controlsDisabled || !onArClick}
              className={`border-b-2 px-5 py-3 text-sm font-semibold ${
                activeTab === 'ar'
                  ? 'border-[#7A003C] font-bold text-[#7A003C]'
                  : 'border-transparent'
              } ${
                onArClick && activeTab !== 'ar'
                  ? `text-[#7B6B72] transition-colors hover:border-[#7A003C] hover:text-[#7A003C]${controlsDisabled ? ' cursor-not-allowed' : ''}`
                  : !onArClick
                    ? 'cursor-not-allowed text-[#A99CA2]'
                    : ''
              }`}
            >
              AR
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={openFeedbackModal}
          disabled={controlsDisabled || feedbackLoading}
          aria-label="Provide feedback on the active SmartMorning dashboard"
          className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-md bg-transparent text-[#7A003C]/70 transition-colors hover:bg-[#FAF7F1] hover:text-[#7A003C] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7A003C]/25 disabled:cursor-not-allowed disabled:opacity-50"
          title={`Provide feedback on the ${activeDashboardLabel} dashboard`}
        >
          <MessageSquare className="h-3.5 w-3.5" />
        </button>
      </div>
      </div>

      {activeTab === 'ar' ? (arBody) : activeTab === 'operations' ? (
        operationsBody
      ) : contentOverride ? (
        <div className="mt-5">
          {contentOverride}
        </div>
      ) : (
        <>

      {activeTab === 'commercial' && <CommercialContent topKpis={topKpis} supply={supply} bridgeRows={bridgeRows} bridgeChart={bridgeChart} growthRows={growthRows} customerRows={commercialCustomerRows} customerUnitRows={commercialCustomerUnitRows} productRows={commercialProductRows} productUnitRows={commercialProductUnitRows} categoryRows={commercialCategoryRows} categoryUnitRows={commercialCategoryUnitRows} growthInfo={commercialGrowthInfo} customerInfo={commercialCustomerInfo} customerUnitInfo={commercialCustomerUnitInfo} productInfo={commercialProductInfo} productUnitInfo={commercialProductUnitInfo} categoryInfo={commercialCategoryInfo} categoryUnitInfo={commercialCategoryUnitInfo} comparison={comparison} loading={loading} onShowInfo={showMetricInfo} onShowChartInfo={showChartInfo} />}

      {activeTab === 'finance' && <div className="mt-5 grid gap-0 overflow-hidden rounded-md border border-[#EDE6E1] lg:grid-cols-4">
        <MetricCard metric={topKpis[0]} icon={BarChart3} comparison={comparison} onShowInfo={showMetricInfo} loading={loading && !metricIsLoaded(topKpis[0])} />
        <MetricCard metric={topKpis[1]} icon={BarChart3} comparison={comparison} onShowInfo={showMetricInfo} loading={loading && !metricIsLoaded(topKpis[1])} />
        <MetricCard metric={topKpis[2]} icon={DollarSign} comparison={comparison} onShowInfo={showMetricInfo} loading={loading && !metricIsLoaded(topKpis[2])} />
        <MetricCard metric={topKpis[3]} icon={Users} comparison={comparison} onShowInfo={showMetricInfo} comingSoon={metricIsComingSoon(topKpis[3])} loading={loading && !metricIsLoaded(topKpis[3])} />
      </div>}

      {activeTab === 'finance' && <div className={`mt-4 grid gap-4 ${SHOW_EXECUTIVE_INSIGHTS ? 'xl:grid-cols-[1.08fr_1.15fr_0.78fr]' : 'xl:grid-cols-2'}`}>
        <div className="rounded-md border border-[#EDE6E1] bg-white">
          <SectionHeader number="1" title="Purposeful & Planned Growth" subtitle="Grow in the right customers, categories and channels" icon={BarChart3} />
          <div className="p-4">
            <h3 className="text-sm font-bold uppercase text-[#7A003C]">Net Sales Bridge</h3>
            <div className="text-xs text-[#7B6B72]">in $ Millions</div>
            <SimpleWaterfall rows={bridgeRows} chart={bridgeChart} onShowInfo={showChartInfo} loading={loading && bridgeRows.length === 0} />
            <div className="mt-4 grid gap-4 md:grid-cols-[1fr_0.7fr]">
              <div>
                <h4 className="text-sm font-bold uppercase text-[#7A003C]">Top 5 Customers by Product Contribution</h4>
                <NumberTable rows={customerRows} kind="customers" maxRows={5} comparison={comparison} info={customerInfo} onShowInfo={showChartInfo} loading={loading && customerRows.length === 0} />
              </div>
              <div className="flex flex-col items-center justify-center rounded-md border border-[#EDE6E1] p-4 text-center">
                <div className="flex items-center justify-center gap-1 text-xs font-bold uppercase text-[#7A003C]"><span>Total Volume</span><InfoButton label="Total Volume" onClick={() => showChartInfo(totalVolumeInfo)} /></div>
                <Factory className="mt-4 h-12 w-12 text-[#7A003C]" />
                <div className="mt-3 text-3xl font-bold">{loading && volumeValue === '-- CS' ? 'Loading…' : volumeValue}</div>
                <div className={`mt-1 min-h-[16px] text-xs font-bold ${volumeComparison.startsWith('▲') ? 'text-[#0E8F5A]' : 'text-[#C62843]'}`}>
                  {volumeLoading ? 'Fetching data' : volumeComparison}
                </div>
              </div>
            </div>
            <div className="mt-5">
              <h4 className="text-sm font-bold uppercase text-[#7A003C]">Product Contribution by Channel</h4>
              <NumberTable rows={channelRows} kind="channels" maxRows={8} comparison={comparison} info={channelInfo} onShowInfo={showChartInfo} loading={loading && channelRows.length === 0} />
            </div>
          </div>
        </div>

        <div className="rounded-md border border-[#EDE6E1] bg-white">
          <SectionHeader number="2" title="Consistency & Optimization" subtitle="Operate with excellence across the end-to-end value chain" icon={Factory} />
          <div className="p-4">
            <h3 className="text-sm font-bold uppercase text-[#7A003C]">Operational Excellence</h3>
            <div className="mt-3 grid gap-3 md:grid-cols-3">
              <MiniMetric label={ops[0]?.name || 'OEE'} metric={ops[0]} onShowInfo={showMetricInfo} value={metricValue(ops[0], '--')} delta={formatDelta(ops[0], comparison)} good={isPositiveDelta(ops[0], comparison)} loading={loading && !metricIsLoaded(ops[0])} />
              <MiniMetric label={ops[1]?.name || 'Yield'} metric={ops[1]} onShowInfo={showMetricInfo} value={metricValue(ops[1], '--')} delta={formatDelta(ops[1], comparison)} good={isPositiveDelta(ops[1], comparison)} loading={loading && !metricIsLoaded(ops[1])} />
              <MiniMetric label={ops[2]?.name || 'MFG Cost / LB'} metric={ops[2]} onShowInfo={showMetricInfo} value={metricValue(ops[2], '--')} delta={formatDelta(ops[2], comparison)} good={isPositiveDelta(ops[2], comparison)} loading={loading && !metricIsLoaded(ops[2])} />
              <MiniMetric
                label={directLaborProductivity?.name || 'Labor Productivity'}
                metric={directLaborProductivity}
                onShowInfo={showMetricInfo}
                value={metricValue(directLaborProductivity, '--')}
                delta={formatDelta(directLaborProductivity, comparison)}
                good={isPositiveDelta(directLaborProductivity, comparison)}
                comingSoon={metricIsComingSoon(directLaborProductivity)}
                loading={loading && !metricIsLoaded(directLaborProductivity)}
              />
              <MiniMetric label={ops[3]?.name || 'MUV'} metric={ops[3]} note={ops[3]?.scope_note} onShowInfo={showMetricInfo} value={metricValue(ops[3], '--')} delta={formatDelta(ops[3], comparison)} good={isPositiveDelta(ops[3], comparison)} loading={loading && !metricIsLoaded(ops[3])} />
            </div>
            <h3 className="mt-6 text-sm font-bold uppercase text-[#7A003C]">Supply Chain Performance</h3>
            <div className="mt-3 grid gap-3 md:grid-cols-3">
              <MiniMetric label={supply[0]?.name || 'Case Fill'} metric={supply[0]} onShowInfo={showMetricInfo} value={metricValue(supply[0], '--')} delta={formatDelta(supply[0], comparison)} good={isPositiveDelta(supply[0], comparison)} loading={loading && !metricIsLoaded(supply[0])} />
              <MiniMetric label={supply[1]?.name || 'Finished Goods Inventory'} metric={supply[1]} onShowInfo={showMetricInfo} value={metricValue(supply[1], '--')} delta={formatDelta(supply[1], comparison)} good={isPositiveDelta(supply[1], comparison)} loading={loading && !metricIsLoaded(supply[1])} />
              <MiniMetric label={supply[2]?.name || 'Raw & Packaging Inventory'} metric={supply[2]} onShowInfo={showMetricInfo} value={metricValue(supply[2], '--')} delta={formatDelta(supply[2], comparison)} good={isPositiveDelta(supply[2], comparison)} loading={loading && !metricIsLoaded(supply[2])} />
              <MiniMetric label={supply[3]?.name || 'Freight Cost % of Sales'} metric={supply[3]} onShowInfo={showMetricInfo} value={metricValue(supply[3], '--')} delta={formatDelta(supply[3], comparison)} good={isPositiveDelta(supply[3], comparison)} loading={loading && !metricIsLoaded(supply[3])} />
              <MiniMetric label={supply[4]?.name || 'PONC'} metric={supply[4]} note={supply[4]?.scope_note} onShowInfo={showMetricInfo} value={metricValue(supply[4], '--')} delta={formatDelta(supply[4], comparison)} good={isPositiveDelta(supply[4], comparison)} loading={loading && !metricIsLoaded(supply[4])} />
            </div>
          </div>
        </div>

        {SHOW_EXECUTIVE_INSIGHTS && (
          <div className="rounded-md border border-[#EDE6E1] bg-white p-5">
            <div className="mb-4 flex items-center gap-3 text-[#7A003C]">
              <Lightbulb className="h-8 w-8" />
              <h3 className="text-lg font-bold uppercase">Executive Insights</h3>
            </div>
            <div className="space-y-6">
              {insights.length === 0 && (
                <div className="rounded-md border border-dashed border-[#E6DCD6] p-4 text-sm font-semibold text-[#7B6B72]">
                  Executive insights are unavailable until validated source metrics are hydrated.
                </div>
              )}
              {insights.map((item: any, index: number) => {
                const icons = [ClipboardList, Lightbulb, AlertTriangle, Factory];
                const Icon = icons[index] || ClipboardList;
                return (
                  <div key={item.prompt} className="grid grid-cols-[2rem_1fr] gap-3">
                    <Icon className="mt-1 h-7 w-7 text-[#7A003C]" />
                    <div>
                      <div className="text-sm font-bold uppercase text-[#7A003C]">{item.prompt}</div>
                      <p className="mt-2 text-sm font-semibold leading-6 text-[#2B2027]">{item.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>}

      {SHOW_ASK_SMARTMORNING && (
        <div className="mt-4 grid gap-4 rounded-md border border-[#EDE6E1] bg-white p-4 lg:grid-cols-[18rem_1fr]">
          <div className="flex items-center gap-4 border-b border-[#EDE6E1] pb-4 lg:border-b-0 lg:border-r lg:pb-0 lg:pr-4">
            <Sparkles className="h-11 w-11 text-[#7A003C]" />
            <div>
              <div className="text-lg font-bold text-[#7A003C]">Ask SmartMorning</div>
              <div className="text-xs font-semibold text-[#2B2027]">Your AI assistant for instant insights, analysis and recommendations across all metrics.</div>
              <div className="mt-1 text-xs font-bold text-[#0E8F5A]">● Connected to enterprise data</div>
            </div>
          </div>
          <div>
            <div className="mb-2 text-sm font-bold text-[#2B2027]">Ask anything about our performance...</div>
            <div className="flex gap-2">
              <input
                className="h-12 flex-1 rounded-md border border-[#EDE6E1] px-4 text-sm outline-none"
                placeholder="e.g., Why did product contribution margin decline?"
              />
              <button className="flex h-12 w-14 items-center justify-center rounded-md bg-[#7A003C] text-white">
                <Send className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              {['Explain net sales vs plan', 'Why is finished goods inventory above plan?', 'Which customers are driving growth?', 'Which plants are at risk?'].map((item) => (
                <span key={item} className="rounded-md bg-[#F8F4F1] px-3 py-2 font-semibold text-[#4B3C44]">{item}</span>
              ))}
            </div>
          </div>
        </div>
      )}
        </>
      )}
      <AlertDialog
        open={refreshConfirmOpen}
        onOpenChange={setRefreshConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Refresh dashboard data?</AlertDialogTitle>
            <AlertDialogDescription>
              This will start a fresh SmartMorning data refresh for the selected period and comparison.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmForceRefresh}
              disabled={forceRefreshDisabled}
              className="bg-[#7A003C] text-white hover:bg-[#650032]"
            >
              Refresh
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => {
          setShowFeedbackModal(false);
          setFeedbackError(null);
        }}
        onSubmit={handleFeedbackSubmit}
        isLoading={feedbackLoading}
        title={`Rate the ${activeDashboardLabel} dashboard`}
        subtitle="Share feedback for this dashboard."
        placeholder="Add a comment (required)"
      />
      <LineagePanel context={lineageContext} onClose={() => setLineageContext(null)} />
    </div>
  );
}
